import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import jsPDF from "jspdf";
import { patchJsPdfText, sanitizePDFText } from "../lib/pdfTextSanitize";
import {
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Download,
  ArrowLeft,
  Calendar,
  Shield,
  MinusCircle,
  RefreshCw,
  Eye,
  FileDown,
  User,
  Building2,
  BookOpenCheck,
  Mail,
  Phone,
  MapPin,
  TrendingUp,
  DollarSign,
  Scale,
  MessageSquare,
  Car,
  TrendingDown,
  BarChart3,
  Info,
  RotateCw,
  MessageCircleQuestion,
  Ticket
} from "lucide-react";
import { ClienteAtencionWatcher } from "../contexts/AtencionPrioritariaContext";

const MAX_POLL_ATTEMPTS = 200; // ~10 minutes at 3s interval

/**
 * Normaliza valores de limites/deducibles para comparacion semantica:
 *  - "RD$50,000", "50,000", "50000.00", "RD$ 50000" -> "50000"
 *  - "No especificado", "---", null, undefined, "" -> ""
 *  - "X", "Si", "Incluido", "Incluida" -> "incluido"
 * Asi "Limite: 50,000" y "Limite: RD$50,000" se reconocen como iguales.
 */
const _NORM_PLACEHOLDERS = new Set([
  "", "-", "--", "---", "n/a", "na", "no especificado", "no especificada",
  "no indicado", "no indicada", "no indica", "no indicado.", "no indicada.",
  "sin especificar", "sin indicar", "no informado", "no informada", "no informa",
  "sin información", "sin informacion", "no disponible", "n.d.", "nd",
  "no aplica", "no aplica.", "ninguno", "ninguna", "rd$0.00", "rd$0", "rd$ 0",
  "us$0.00", "us$0", "us$ 0", "0.00", "0", "0.0",
]);
const _NORM_INCLUIDO = new Set([
  "x", "si", "sí", "incluido", "incluida", "incluye", "marcado", "marcada", "yes", "true", "1",
  "incluida (marcado x, sin monto)", "incluido (marcado x, sin monto)",
  "marcado con x", "marcada con x", "marcado x", "marcada x", "viene marcado", "viene marcada",
  // Marcadores específicos de Seguros Universal y aseguradoras dominicanas
  "cma", "c.m.a.", "cma.", "ilimitado", "ilimitada", "sin límite", "sin limite",
  "según tabla", "segun tabla", "según tabla de servicios", "segun tabla de servicios",
]);
// Patterns regex que indican "INCLUIDO / Marcado / Activo" en cualquier redacción
const _INCLUIDO_PATTERNS = [
  /^\s*x\s*$/i,
  /^marcad[oa]\s+con\s+x\s*$/i,
  /^x\s+marcad[oa]\s*$/i,
  /^incluid[oa]\s*(con\s+x)?$/i,
  /\bmarcad[oa]\s+con\s+x\b/i,
  /\bx\s+incluid[oa]\b/i,
  // "Máximo por cada servicio RD$X" / "Maximo por cada Servicio" → incluido con límite
  /^m[áa]ximo\s+por\s+cada\s+servicio/i,
  /^m[áa]ximo\s+por\s+servicio/i,
  /^hasta\s+rd\$?\s*[\d,\.]+\s*por\s+servicio/i,
  // "CMA" / "C.M.A." en cualquier posición
  /^c\.?\s*m\.?\s*a\.?\b/i,
];
const normalizeCoverageValue = (v) => {
  if (v == null) return "";
  let s = String(v).trim().toLowerCase();
  if (!s) return "";
  // Quitar contenido entre paréntesis al final que solo aclara redacción.
  // "Incluida (sin monto especificado)" → "Incluida"
  // "X (incluido, sin monto especificado)" → "X"
  // "30 días (según tabla de Servicios)" → "30 días"
  s = s.replace(/\s*\([^)]*\)\s*$/g, "").trim();
  // Quitar sufijos "según tabla...", "según observaciones", "según anexo", "(...)"
  s = s.replace(/\s*seg[úu]n\s+(la\s+)?(tabla|observaciones|observ|anexo|el\s+anexo|cobertura).*$/i, "").trim();
  // Quitar comillas residuales
  s = s.replace(/['"`'']/g, "").trim();
  // Quitar prefijos de moneda y espacios
  s = s.replace(/rd\$?|us\$?|us\s*d|usd|dop|\$/gi, "");
  s = s.replace(/\s+/g, " ").trim();
  if (_NORM_PLACEHOLDERS.has(s)) return "";
  if (_NORM_INCLUIDO.has(s)) return "incluido";
  // Detectar patrones "Marcado con X" / "X" / "Incluido con X" en cualquier redacción
  for (const pattern of _INCLUIDO_PATTERNS) {
    if (pattern.test(s)) return "incluido";
  }
  // Si es numerico (con miles/decimales), normalizar a numero
  // Convencion DR: coma = separador de miles, punto = decimal (formato US).
  // "50,000" -> 50000, "50,000.00" -> 50000, "50000.00" -> 50000
  const numMatch = s.match(/^-?[\d.,]+$/);
  if (numMatch) {
    const cleaned = s.replace(/,/g, ""); // quitar SOLO comas (miles), preservar punto decimal
    const n = Number(cleaned);
    if (Number.isFinite(n)) return String(n);
  }
  return s;
};

/**
 * Extrae tokens semanticos (porcentajes + montos numericos) de un texto libre.
 * Permite comparar valores con redaccion distinta pero mismo significado.
 *  - "1% del valor asegurado, minimo RD$10,000.00" -> ["1%", "10000"]
 *  - "1% (minimo RD$10,000.00)"                    -> ["1%", "10000"]
 *  - "10% minimo RD$500.00"                         -> ["10%", "500"]
 *  - "10% (minimo RD$500.00)"                       -> ["10%", "500"]
 */
const extractSemanticTokens = (v) => {
  if (v == null) return [];
  const text = String(v).toLowerCase();
  const tokens = new Set();
  // Porcentajes: "1%", "10%", "1.5%"
  const pcts = text.match(/-?\d+(?:[.,]\d+)?\s*%/g) || [];
  pcts.forEach((p) => {
    tokens.add(p.replace(/\s+/g, "").replace(",", "."));
  });
  // Montos: numeros con o sin prefijo de moneda (RD$, US$, $)
  // Capturamos secuencias numericas y luego decidimos cuales son montos validos
  const amounts = text.match(/\d[\d.,]*/g) || [];
  amounts.forEach((raw) => {
    // Si ya esta capturado como parte de un porcentaje, lo evitamos
    const cleaned = raw.replace(/,/g, ""); // miles -> sin coma
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n === 0) return;
    // Si el numero termina seguido de %, ya esta capturado por el regex de porcentajes
    const idx = text.indexOf(raw);
    const trailing = text.substring(idx + raw.length).trimStart();
    if (trailing.startsWith("%")) return;
    tokens.add(String(n));
  });
  return Array.from(tokens).sort();
};

/**
 * Compara dos valores con normalizacion semantica robusta:
 *  1. Comparacion directa de la forma normalizada (numeros, placeholders).
 *  2. Si fallan, comparar por tokens semanticos extraidos (porcentajes+montos).
 */
const coverageValuesEqual = (a, b) => {
  const na = normalizeCoverageValue(a);
  const nb = normalizeCoverageValue(b);
  if (na === nb) return true;
  // Fallback semantico: extraer porcentajes + montos
  const ta = extractSemanticTokens(a);
  const tb = extractSemanticTokens(b);
  if (ta.length === 0 && tb.length === 0) {
    // Sin numeros en ambos lados: ya sabiamos que normalizados no eran iguales,
    // pero si uno era placeholder y otro tenia texto adicional, los normalizados ya devolvieron "".
    // Si llegamos aqui, los textos son realmente distintos a nivel de palabras (no de cifras).
    return na === "" && nb === "";
  }
  if (ta.length !== tb.length) return false;
  return ta.every((v, i) => v === tb[i]);
};

/**
 * Compara una cobertura entre dos polizas y devuelve un texto describiendo
 * exactamente que difiere. Si todo es igual (con normalizacion), devuelve un
 * mensaje claro "No existen diferencias en la comparacion".
 */
const compareCoverageObservation = (cob) => {
  const aLim = cob?.poliza_a_limite;
  const bLim = cob?.poliza_b_limite;
  const aDed = cob?.poliza_a_deducible;
  const bDed = cob?.poliza_b_deducible;
  const aLimEmpty = normalizeCoverageValue(aLim) === "" && extractSemanticTokens(aLim).length === 0;
  const bLimEmpty = normalizeCoverageValue(bLim) === "" && extractSemanticTokens(bLim).length === 0;
  const aDedEmpty = normalizeCoverageValue(aDed) === "" && extractSemanticTokens(aDed).length === 0;
  const bDedEmpty = normalizeCoverageValue(bDed) === "" && extractSemanticTokens(bDed).length === 0;
  // Si la cobertura solo esta en una poliza (limite presente en una y vacio en la otra Y los deducibles iguales o vacios)
  const onlyA = !aLimEmpty && bLimEmpty && aDedEmpty === bDedEmpty;
  const onlyB = aLimEmpty && !bLimEmpty && aDedEmpty === bDedEmpty;
  if (onlyA) return "Solo en Póliza Actual";
  if (onlyB) return "Solo en Renovación";
  // Comparar limite y deducible
  const limEq = coverageValuesEqual(aLim, bLim);
  const dedEq = coverageValuesEqual(aDed, bDed);
  if (limEq && dedEq) return "No existen diferencias en la comparación";
  const diffs = [];
  if (!limEq) diffs.push(`Límite: "${aLim || "—"}" vs "${bLim || "—"}"`);
  if (!dedEq) diffs.push(`Deducible: "${aDed || "—"}" vs "${bDed || "—"}"`);
  return `Diferencia en ${diffs.join("; ")}`;
};

// Considera un valor "real" (no vacío ni placeholder tipo "No especificado").
const isMeaningfulValue = (v) => normalizeCoverageValue(v) !== "";

// Filtra filas de la tabla comparativa: elimina "Forma de Pago", "NCF" y
// las filas de Deducible sin valor real en ninguna de las dos pólizas.
const filterTablaComparativa = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => {
    const item = String(row?.item || "").trim().toLowerCase();
    if (item.includes("forma de pago") || item.includes("ncf")) return false;
    if (item.includes("deducible") && !isMeaningfulValue(row?.poliza_a) && !isMeaningfulValue(row?.poliza_b)) return false;
    return true;
  });
};

// Mantiene en "Diferencias Clave" únicamente la comparación de primas
// (más económica / más cara, porcentaje y montos).
const PRIMA_DIFF_RE = /(prima|m[áa]s\s+(econ[óo]mic|car|costos)|porcentaje|%)/i;
const filterDiferenciasPrima = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter((d) => PRIMA_DIFF_RE.test(String(d || "")));
};

export default function ComparisonResultPage() {
  const { id } = useParams();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [reconsultando, setReconsultando] = useState(false);
  const [reanalizando, setReanalizando] = useState(false);
  const [ticketPrompt, setTicketPrompt] = useState(false);
  const [creandoTicket, setCreandoTicket] = useState(false);
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const pollCountRef = useRef(0);
  const comparisonRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    comparisonRef.current = comparison;
  }, [comparison]);

  // Cleanup on unmount - critical to prevent memory leaks
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const handleReconsultarPrecios = useCallback(async () => {
    if (!comparison?.id || reconsultando) return;
    setReconsultando(true);
    try {
      const res = await axios.post(
        `${API}/comparisons/${comparison.id}/reconsultar-precios`,
        {},
        getAuthHeaders()
      );
      if (!mountedRef.current) return;
      setComparison((prev) => (prev ? { ...prev, precios_mercado: res.data.precios_mercado } : prev));
      const total = res.data.precios_mercado?.total_vehiculos_analizados || 0;
      toast.success(`Precios actualizados — ${total} anuncio${total === 1 ? '' : 's'} analizado${total === 1 ? '' : 's'}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al re-consultar precios";
      toast.error(msg);
    } finally {
      if (mountedRef.current) setReconsultando(false);
    }
  }, [comparison?.id, reconsultando, getAuthHeaders]);

  const fetchComparison = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comparisons/${id}`, getAuthHeaders());
      if (!mountedRef.current) return;
      
      setComparison(res.data);
      setLoading(false);
      
      if (res.data.status === "processing") {
        pollCountRef.current += 1;
        setPollCount(pollCountRef.current);
        
        if (pollCountRef.current < MAX_POLL_ATTEMPTS) {
          pollTimerRef.current = setTimeout(fetchComparison, 3000);
        } else {
          toast.error("El análisis está tomando demasiado tiempo. Intente refrescar la página más tarde.");
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      // Keep loading=true to avoid flashing "Comparación no encontrada"
      toast.error("Error al cargar la comparación");
      navigate("/");
    }
  }, [id, getAuthHeaders, navigate]);

  useEffect(() => {
    // Reset poll count on new comparison
    pollCountRef.current = 0;
    setPollCount(0);
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    fetchComparison();
    
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [id, fetchComparison]);

  const handleReanalizarConIA = useCallback(async () => {
    if (!comparison?.id || reanalizando) return;
    if (!window.confirm("¿Re-analizar con IA? Esto volverá a ejecutar el análisis sobre los documentos originales y reemplazará los resultados actuales.")) return;
    setReanalizando(true);
    try {
      await axios.post(
        `${API}/comparisons/${comparison.id}/reanalizar`,
        {},
        getAuthHeaders()
      );
      toast.success("Re-análisis iniciado. Procesando…");
      pollCountRef.current = 0;
      setPollCount(0);
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      setComparison((prev) => prev ? { ...prev, status: "processing" } : prev);
      fetchComparison();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al iniciar el re-análisis";
      toast.error(msg);
    } finally {
      if (mountedRef.current) setReanalizando(false);
    }
  }, [comparison?.id, reanalizando, getAuthHeaders, fetchComparison]);

  const [reprocessing, setReprocessing] = useState(false);
  const handleReprocesarDeterministico = useCallback(async () => {
    if (!comparison?.id || reprocessing) return;
    if (!window.confirm(
      "¿Re-procesar coberturas con motor determinístico?\n\n" +
      "Esto vuelve a aplicar el parser exacto (regex) sobre el texto ya extraído de los PDFs " +
      "y limpia datos antiguos guardados con la IA. NO vuelve a llamar al LLM (es rápido)."
    )) return;
    setReprocessing(true);
    try {
      const resp = await axios.post(
        `${API}/comparisons/${comparison.id}/reprocess`,
        {},
        getAuthHeaders()
      );
      const s = resp?.data?.summary || {};
      const warning = resp?.data?.warning;
      if (warning) {
        toast.warning(
          `Re-procesado parcial: aplicada deduplicación y normalización, pero los parsers no extrajeron datos nuevos. Use "Re-analizar con IA" para regenerar valores.`,
          { duration: 8000 }
        );
      } else {
        toast.success(
          `Re-procesado: ${s.coberturas_parsed || 0} coberturas, ${s.servicios_parsed || 0} servicios, ${s.vehiculos_parsed || 0} vehículos.`
        );
      }
      // Recargar la comparación para mostrar los datos limpios
      await fetchComparison();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al re-procesar la comparación";
      toast.error(msg);
    } finally {
      if (mountedRef.current) setReprocessing(false);
    }
  }, [comparison?.id, reprocessing, getAuthHeaders, fetchComparison]);

  const openGenerateDialog = async () => {
    // Generate the same PDF as "Descargar PDF" and save it to the backend
    setGenerating(true);
    try {
      // Generate informe ejecutivo if not yet available
      let comp = comparison;
      if (!comp.informe_ejecutivo) {
        comp = await generateInformeEjecutivo();
      }
      
      const doc = isDetailedFormat ? generateDetailedPDF(comp) : generateLegacyPDF();
      if (!doc) {
        toast.error("Error generando el PDF");
        return;
      }
      
      // Convert jsPDF to base64
      const pdfBase64 = doc.output("datauristring").split(",")[1];
      
      const payload = {
        comparison_id: id,
        report_type: "executive",
        pdf_data: pdfBase64
      };
      
      await axios.post(`${API}/reports`, payload, getAuthHeaders());
      
      toast.success("Informe guardado exitosamente");
      afterReportSaved();
    } catch (err) {
      console.error("Error saving report:", err);
      toast.error(err.response?.data?.detail || "Error al guardar el informe");
    } finally {
      setGenerating(false);
    }
  };

  // Regenera el PDF de un Informe ya guardado (uso: para corregir el bug de
  // letter-spacing en informes guardados con la version vieja del generator)
  const regenerateExistingReport = async (reportId) => {
    setGenerating(true);
    try {
      let comp = comparisonRef.current;
      if (!comp) {
        toast.error("No se pudo cargar la comparacion");
        return;
      }
      if (!comp.informe_ejecutivo) {
        comp = await generateInformeEjecutivo();
      }
      const doc = isDetailedFormat ? generateDetailedPDF(comp) : generateLegacyPDF();
      if (!doc) {
        toast.error("Error generando el PDF");
        return;
      }
      const pdfBase64 = doc.output("datauristring").split(",")[1];
      await axios.put(
        `${API}/reports/${reportId}/pdf-data`,
        { pdf_data: pdfBase64 },
        getAuthHeaders()
      );
      toast.success("Informe regenerado con texto corregido");
      navigate("/reports");
    } catch (err) {
      console.error("Error regenerating report:", err);
      toast.error(err.response?.data?.detail || "Error al regenerar el informe");
    } finally {
      setGenerating(false);
    }
  };

  // Detecta el query param ?regenerateReportId=XXX y lanza la regeneracion
  // automaticamente una vez la comparacion termina de cargar.
  const regenTriggeredRef = useRef(false);
  useEffect(() => {
    if (regenTriggeredRef.current) return;
    if (!comparison || comparison.status === "processing") return;
    const params = new URLSearchParams(window.location.search);
    const reportId = params.get("regenerateReportId");
    if (!reportId) return;
    regenTriggeredRef.current = true;
    regenerateExistingReport(reportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparison]);

  // Auto-generar Informe Ejecutivo al cargar la comparacion completada
  // (evita que el usuario tenga que ir al tab "Informe Ejecutivo" y click "Generar").
  const autoGenInformeRef = useRef(false);
  useEffect(() => {
    if (autoGenInformeRef.current) return;
    if (!comparison) return;
    if (comparison.status !== "completed") return;
    if (comparison.informe_ejecutivo) return; // ya esta generado
    if (generatingInforme) return;             // ya esta en curso
    autoGenInformeRef.current = true;
    // Pequeño delay para no chocar con otros fetches iniciales
    const t = setTimeout(() => {
      generateInformeEjecutivo();
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparison?.id, comparison?.status, comparison?.informe_ejecutivo]);

  // Tras guardar el informe: si hay diferencias y aún no hay ticket, ofrecer crear ticket;
  // de lo contrario, navegar a la lista de informes.
  const afterReportSaved = () => {
    const comp = comparisonRef.current || comparison;
    const hayDiferencias = (comp?.differences || []).length > 0;
    if (hayDiferencias && !comp?.ticket_numero) {
      setTicketPrompt(true);
    } else {
      navigate("/reports");
    }
  };

  const handleDecisionTicket = async (crear) => {
    setCreandoTicket(true);
    try {
      const res = await axios.post(
        `${API}/comparisons/${id}/decision-ticket`,
        { crear },
        getAuthHeaders()
      );
      if (crear && res.data?.ticket_numero) {
        toast.success(`Ticket #${res.data.ticket_numero} creado en Mesa de Ayuda`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar la solicitud");
    } finally {
      setCreandoTicket(false);
      setTicketPrompt(false);
      navigate("/reports");
    }
  };

  // Check if using new detailed format
  const isDetailedFormat = comparison?.analysis_format === "detailed_report";

  const generateDetailedPDF = (compOverride) => {
    const comparison = compOverride || comparisonRef.current;
    if (!comparison) return null;
    
    const doc = patchJsPdfText(new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" }));
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const PAGE_MARGIN_BOTTOM = 25;
    const PAGE_MARGIN_TOP = 15;
    let yPos = PAGE_MARGIN_TOP;
    
    const getRemainingSpace = () => pageHeight - PAGE_MARGIN_BOTTOM - yPos;
    
    const addNewPage = () => {
      doc.addPage();
      yPos = PAGE_MARGIN_TOP;
    };
    
    const checkNewPage = (neededSpace = 30) => {
      if (yPos + neededSpace > pageHeight - PAGE_MARGIN_BOTTOM) {
        addNewPage();
        return true;
      }
      return false;
    };

    const addSectionTitle = (title, minContentSpace = 40) => {
      const titleBlockHeight = 20 + minContentSpace;
      if (getRemainingSpace() < titleBlockHeight) {
        addNewPage();
      }
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 74, 153);
      doc.text(title, 15, yPos);
      yPos += 2;
      doc.setDrawColor(224, 224, 224);
      doc.line(15, yPos, pageWidth - 15, yPos);
      yPos += 8;
      doc.setTextColor(0, 0, 0);
    };

    const addText = (text, indent = 15, fontSize = 9) => {
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(text, pageWidth - indent - 15);
      lines.forEach(line => {
        checkNewPage(6);
        doc.text(line, indent, yPos);
        yPos += 5;
      });
    };

    // ====== Corporate branding: header + footer on every page ======
    const generatedAt = new Date();
    const generatedAtStr = generatedAt.toLocaleString("es-DO", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit"
    });
    const userName = user?.name || user?.email || "Usuario";
    const COMPANY_NAME = "Qasesores Corredores de Seguros SRL";
    const applyBranding = () => {
      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        // Header strip (corporate blue)
        doc.setFillColor(25, 57, 102); // #193966
        doc.rect(0, 0, pageWidth, 8, "F");
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(255, 255, 255);
        doc.text(COMPANY_NAME, 15, 5.5);
        doc.text(`Generado: ${generatedAtStr}`, pageWidth - 15, 5.5, { align: "right" });
        // Footer strip
        doc.setFillColor(25, 57, 102);
        doc.rect(0, pageHeight - 10, pageWidth, 10, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(`Usuario: ${userName}`, 15, pageHeight - 4);
        doc.text(`Página ${p} de ${totalPages}`, pageWidth - 15, pageHeight - 4, { align: "right" });
        // Green accent line
        doc.setFillColor(128, 188, 0); // #80bc00
        doc.rect(0, pageHeight - 11, pageWidth, 1, "F");
        doc.setTextColor(0, 0, 0);
      }
    };

    // ===== NUEVO INFORME EJECUTIVO (Página 1) =====
    // Enfocado en hallazgos principales: valor asegurado, deducibles, primas, coberturas, condiciones especiales
    if (comparison.informe_ejecutivo) {
      const ie = comparison.informe_ejecutivo;
      yPos = 18; // leave room for corporate header strip

      // Big title bar
      doc.setFillColor(25, 57, 102); // #193966
      doc.rect(0, yPos - 2, pageWidth, 14, "F");
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("INFORME EJECUTIVO", pageWidth / 2, yPos + 7, { align: "center" });
      doc.setTextColor(0, 0, 0);
      yPos += 20;

      // Subtitle — policy headers if available
      const pA = comparison.datos_generales_polizas?.[0] || {};
      const pB = comparison.datos_generales_polizas?.[1] || {};
      if (pA.aseguradora || pB.aseguradora) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(80, 80, 80);
        const subt = `Comparativa: ${pA.aseguradora || "Póliza Actual"} vs ${pB.aseguradora || "Renovación"}`;
        doc.text(subt, pageWidth / 2, yPos, { align: "center" });
        doc.setTextColor(0, 0, 0);
        yPos += 8;
      }

      // Resumen General: eliminado por requerimiento del cliente.

      // Hallazgos agrupados por las 5 categorías clave
      const CATEGORIAS_CLAVE = ["Valor Asegurado", "Deducibles", "Primas", "Coberturas", "Condiciones Especiales"];
      const hallazgos = Array.isArray(ie.hallazgos) ? ie.hallazgos : [];
      const hallazgosPorCat = {};
      CATEGORIAS_CLAVE.forEach(c => { hallazgosPorCat[c] = []; });
      hallazgos.forEach(h => {
        const cat = CATEGORIAS_CLAVE.find(c => (h.categoria || "").toLowerCase() === c.toLowerCase());
        if (cat) hallazgosPorCat[cat].push(h);
      });

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(25, 57, 102);
      doc.text("HALLAZGOS PRINCIPALES", 15, yPos);
      yPos += 2;
      doc.setDrawColor(128, 188, 0);
      doc.setLineWidth(0.6);
      doc.line(15, yPos, 80, yPos);
      doc.setLineWidth(0.2);
      yPos += 6;
      doc.setTextColor(0, 0, 0);

      const impactColors = { alto: [220, 38, 38], medio: [217, 119, 6], bajo: [22, 163, 74] };

      CATEGORIAS_CLAVE.forEach(cat => {
        const items = hallazgosPorCat[cat];
        if (!items || items.length === 0) return;
        checkNewPage(12 + items.length * 8);
        // Category pill
        doc.setFillColor(25, 57, 102);
        doc.roundedRect(15, yPos - 3, 55, 6, 1.5, 1.5, "F");
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text(cat.toUpperCase(), 17, yPos + 1);
        doc.setTextColor(0, 0, 0);
        yPos += 8;
        // Items
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        items.forEach(h => {
          checkNewPage(10);
          const imp = impactColors[h.impacto] || [120, 120, 120];
          doc.setFillColor(imp[0], imp[1], imp[2]);
          doc.circle(17, yPos - 1, 1.2, "F");
          const lines = doc.splitTextToSize(h.descripcion || "", pageWidth - 45);
          lines.forEach((line, i) => {
            if (i > 0) checkNewPage(5);
            doc.text(line, 22, yPos);
            yPos += 4.5;
          });
          yPos += 2;
        });
        yPos += 3;
      });

      // Recomendación Ejecutiva
      if (ie.recomendacion_ejecutiva) {
        checkNewPage(30);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(25, 57, 102);
        doc.text("RECOMENDACIÓN EJECUTIVA", 15, yPos);
        yPos += 2;
        doc.setDrawColor(128, 188, 0);
        doc.setLineWidth(0.6);
        doc.line(15, yPos, 85, yPos);
        doc.setLineWidth(0.2);
        yPos += 6;
        doc.setTextColor(0, 0, 0);

        doc.setFillColor(232, 245, 233); // light green
        const recLines = doc.splitTextToSize(ie.recomendacion_ejecutiva, pageWidth - 40);
        const recH = recLines.length * 5 + 8;
        doc.roundedRect(15, yPos - 4, pageWidth - 30, recH, 2, 2, "F");
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        recLines.forEach(line => {
          doc.text(line, 20, yPos);
          yPos += 5;
        });
        yPos += 6;
      }

      // New page before the rest of the detailed report
      addNewPage();
    }

    // Title
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 74, 153);
    doc.text("ANÁLISIS COMPARATIVO Y RECOMENDACIÓN DE PÓLIZA", pageWidth / 2, yPos, { align: "center" });
    yPos += 15;
    doc.setTextColor(0, 0, 0);

    // Datos del Asegurado
    if (comparison.datos_asegurado) {
      addSectionTitle("DATOS DEL ASEGURADO");
      const da = comparison.datos_asegurado;
      const aseguradoData = [
        ["Nombre / Razón Social", da.nombre_razon_social || "No especificado"],
        ["Identificación (RNC)", da.identificacion_rnc || "No especificado"],
        ["Dirección", da.direccion || "No especificado"],
      ];
      
      doc.setFontSize(9);
      aseguradoData.forEach(([label, value]) => {
        checkNewPage(8);
        doc.setFont("helvetica", "bold");
        doc.text(`${label}:`, 15, yPos);
        doc.setFont("helvetica", "normal");
        const valueLines = doc.splitTextToSize(value, pageWidth - 70);
        doc.text(valueLines[0], 60, yPos);
        if (valueLines.length > 1) {
          valueLines.slice(1).forEach(line => {
            yPos += 5;
            doc.text(line, 60, yPos);
          });
        }
        yPos += 7;
      });
      yPos += 5;
    }

    // Datos del Vehículo (si es póliza de vehículo y es UN solo vehículo)
    const esMultiVehiculo = (comparison.precios_mercado?.total_vehiculos || 0) > 1 || (comparison.precios_mercado?.vehiculos_consultados?.length || 0) > 1;
    if (comparison.datos_vehiculo?.es_poliza_vehiculo && !esMultiVehiculo) {
      addSectionTitle("DATOS DEL VEHÍCULO");
      const dv = comparison.datos_vehiculo;
      
      doc.setFontSize(9);
      const vehiculoData = [
        ["Marca", dv.marca || "N/A"],
        ["Modelo", dv.modelo || "N/A"],
        ["Año", dv.año?.toString() || "N/A"],
        ["Versión/Submodelo", dv.submodelo || "N/A"],
        ["Color", dv.color || "N/A"],
        ["Placa", dv.placa || "N/A"],
        ["Chasis", dv.chasis || "N/A"],
        ["Valor Asegurado", dv.valor_asegurado ? `${dv.moneda_valor_asegurado || dv.moneda_valor_asegurado_normalizada || 'RD$'} ${dv.valor_asegurado.toLocaleString('es-DO')}` : "N/A"],
      ];
      if (dv.tipo_vehiculo) vehiculoData.push(["Tipo Vehículo", dv.tipo_vehiculo]);
      if (dv.cilindraje) vehiculoData.push(["Cilindraje", dv.cilindraje]);
      if (dv.cantidad_pasajeros) vehiculoData.push(["Cant. Pasajeros", dv.cantidad_pasajeros.toString()]);
      if (dv.numero_motor) vehiculoData.push(["No. Motor", dv.numero_motor]);
      if (dv.tipo_combustible) vehiculoData.push(["Combustible", dv.tipo_combustible]);
      if (dv.tipo_traccion) vehiculoData.push(["Tracción", dv.tipo_traccion]);
      if (dv.uso) vehiculoData.push(["Uso", dv.uso]);
      if (dv.domicilio_guarda) vehiculoData.push(["Domicilio de Guarda", dv.domicilio_guarda]);
      
      // Mostrar en 2 columnas
      for (let i = 0; i < vehiculoData.length; i += 2) {
        checkNewPage(8);
        const left = vehiculoData[i];
        const right = vehiculoData[i + 1];
        
        doc.setFont("helvetica", "bold");
        doc.text(`${left[0]}:`, 15, yPos);
        doc.setFont("helvetica", "normal");
        doc.text(left[1], 50, yPos);
        
        if (right) {
          doc.setFont("helvetica", "bold");
          doc.text(`${right[0]}:`, 100, yPos);
          doc.setFont("helvetica", "normal");
          doc.text(right[1], 140, yPos);
        }
        yPos += 7;
      }
      yPos += 5;
    }

    // Análisis de Valor de Mercado
    if (comparison.precios_mercado) {
      addSectionTitle("ANÁLISIS DE VALOR DE MERCADO", 50);

      // ⇨ Fuente de datos
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("Fuente:", 15, yPos);
      doc.setFont("helvetica", "normal");
      const fuenteDatos = comparison.precios_mercado.fuente_datos || comparison.precios_mercado.fuente || "Supercarros (supercarros.com) - Portal de vehículos usados en República Dominicana";
      const fuenteLines = doc.splitTextToSize(fuenteDatos, pageWidth - 45);
      doc.text(fuenteLines[0], 30, yPos);
      if (fuenteLines.length > 1) {
        fuenteLines.slice(1).forEach(ln => { yPos += 5; doc.text(ln, 30, yPos); });
      }
      yPos += 5;

      // ⇨ Fecha de consulta + total analizado
      const totalAnalizados = comparison.precios_mercado.total_vehiculos_analizados
        ?? (comparison.precios_mercado.vehiculos_consultados || []).reduce(
          (sum, v) => sum + (v?.precios_mercado?.vehiculos_encontrados || 0), 0
        );
      const fechaConsultaRaw = comparison.precios_mercado.fecha_consulta;
      let fechaConsultaStr = "";
      if (fechaConsultaRaw) {
        try {
          const d = new Date(fechaConsultaRaw);
          fechaConsultaStr = d.toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' });
        } catch (e) { fechaConsultaStr = fechaConsultaRaw; }
      }
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 100, 100);
      const metaParts = [];
      if (totalAnalizados) metaParts.push(`${totalAnalizados} anuncio${totalAnalizados === 1 ? '' : 's'} analizado${totalAnalizados === 1 ? '' : 's'}`);
      if (fechaConsultaStr) metaParts.push(`Consulta: ${fechaConsultaStr}`);
      if (metaParts.length) {
        doc.text(metaParts.join('  •  '), 15, yPos);
        yPos += 5;
      }
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      yPos += 2;

      // Show tasa de cambio first (applies to both formats)
      if (comparison.precios_mercado.tasa_cambio) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text(`Tasa de cambio: 1 US$ = RD$ ${comparison.precios_mercado.tasa_cambio} (${comparison.precios_mercado.fuente_tasa_cambio || "BCRD"})`, 15, yPos);
        doc.setFont("helvetica", "normal");
        yPos += 8;
      }

      // Multi-vehicle format
      if (comparison.precios_mercado.vehiculos_consultados?.length > 0) {
        const precioColWidths = [45, 45, 45];
        
        comparison.precios_mercado.vehiculos_consultados.forEach((vehiculo, vIdx) => {
          checkNewPage(40);
          
          // Vehicle header
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(30, 58, 95);
          const vTitle = `${vehiculo.marca || ""} ${vehiculo.modelo || ""} ${vehiculo.año || ""}`.trim();
          const vPlaca = vehiculo.placa ? ` - Placa: ${vehiculo.placa}` : "";
          doc.text(`${vTitle}${vPlaca}`, 15, yPos);
          doc.setTextColor(0, 0, 0);
          yPos += 5;
          
          if (vehiculo.valor_asegurado) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            const moneda = vehiculo.moneda_valor_asegurado || 'RD$';
            doc.text(`Valor Asegurado: ${moneda} ${vehiculo.valor_asegurado?.toLocaleString('es-DO')}`, 15, yPos);
            yPos += 5;
          }
          
          const vpm = vehiculo.precios_mercado;
          if (vpm?.exito) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.text(`Vehículos encontrados: ${vpm.vehiculos_encontrados || 0}`, 15, yPos);
            yPos += 6;
            
            // Precios USD
            if (getRemainingSpace() < 28) addNewPage();
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text("PRECIOS EN DÓLARES (US$)", 15, yPos);
            yPos += 6;
            
            doc.setFillColor(37, 99, 235);
            doc.rect(15, yPos - 4, precioColWidths[0], 10, "F");
            doc.setFillColor(22, 163, 74);
            doc.rect(15 + precioColWidths[0], yPos - 4, precioColWidths[1], 10, "F");
            doc.setFillColor(147, 51, 234);
            doc.rect(15 + precioColWidths[0] + precioColWidths[1], yPos - 4, precioColWidths[2], 10, "F");
            
            doc.setFontSize(8);
            doc.setTextColor(255, 255, 255);
            doc.text("PRECIO MÍNIMO", 17, yPos + 2);
            doc.text("PRECIO PROMEDIO", 17 + precioColWidths[0], yPos + 2);
            doc.text("PRECIO MÁXIMO", 17 + precioColWidths[0] + precioColWidths[1], yPos + 2);
            yPos += 10;
            
            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text(`US$ ${vpm.precio_minimo_usd?.toLocaleString('es-DO') || "N/A"}`, 17, yPos);
            doc.text(`US$ ${vpm.precio_promedio_usd?.toLocaleString('es-DO') || "N/A"}`, 17 + precioColWidths[0], yPos);
            doc.text(`US$ ${vpm.precio_maximo_usd?.toLocaleString('es-DO') || "N/A"}`, 17 + precioColWidths[0] + precioColWidths[1], yPos);
            yPos += 12;
            
            // Precios RD$
            if (vpm.precio_minimo_dop) {
              if (getRemainingSpace() < 28) addNewPage();
              doc.setFontSize(8);
              doc.setFont("helvetica", "bold");
              doc.setTextColor(0, 0, 0);
              doc.text("PRECIOS EN PESOS DOMINICANOS (RD$)", 15, yPos);
              yPos += 6;
              
              doc.setFillColor(5, 150, 105);
              doc.rect(15, yPos - 4, precioColWidths[0], 10, "F");
              doc.setFillColor(59, 130, 246);
              doc.rect(15 + precioColWidths[0], yPos - 4, precioColWidths[1], 10, "F");
              doc.setFillColor(168, 85, 247);
              doc.rect(15 + precioColWidths[0] + precioColWidths[1], yPos - 4, precioColWidths[2], 10, "F");
              
              doc.setFontSize(8);
              doc.setTextColor(255, 255, 255);
              doc.text("PRECIO MÍNIMO", 17, yPos + 2);
              doc.text("PRECIO PROMEDIO", 17 + precioColWidths[0], yPos + 2);
              doc.text("PRECIO MÁXIMO", 17 + precioColWidths[0] + precioColWidths[1], yPos + 2);
              yPos += 10;
              
              doc.setTextColor(0, 0, 0);
              doc.setFont("helvetica", "bold");
              doc.setFontSize(10);
              doc.text(`RD$ ${vpm.precio_minimo_dop?.toLocaleString('es-DO') || "N/A"}`, 17, yPos);
              doc.text(`RD$ ${vpm.precio_promedio_dop?.toLocaleString('es-DO') || "N/A"}`, 17 + precioColWidths[0], yPos);
              doc.text(`RD$ ${vpm.precio_maximo_dop?.toLocaleString('es-DO') || "N/A"}`, 17 + precioColWidths[0] + precioColWidths[1], yPos);
              yPos += 12;
            }
            
            // Análisis del valor asegurado
            if (vpm.analisis_valor) {
              if (getRemainingSpace() < 30) addNewPage();
              const av = vpm.analisis_valor;
              let bgColor, textColor, estadoTexto;
              if (av.estado === "DENTRO_DEL_RANGO") {
                bgColor = [220, 252, 231]; textColor = [21, 128, 61]; estadoTexto = "VALOR ASEGURADO ADECUADO";
              } else if (av.estado === "POR_DEBAJO") {
                bgColor = [254, 249, 195]; textColor = [161, 98, 7]; estadoTexto = "VALOR ASEGURADO POR DEBAJO DEL MERCADO";
              } else {
                bgColor = [255, 237, 213]; textColor = [194, 65, 12]; estadoTexto = "VALOR ASEGURADO POR ENCIMA DEL MERCADO";
              }
              doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
              doc.rect(15, yPos - 4, pageWidth - 30, 20, "F");
              doc.setTextColor(textColor[0], textColor[1], textColor[2]);
              doc.setFont("helvetica", "bold");
              doc.setFontSize(9);
              doc.text(estadoTexto, 20, yPos + 2);
              doc.setFont("helvetica", "normal");
              doc.setFontSize(7);
              doc.setTextColor(60, 60, 60);
              const recomLines = doc.splitTextToSize(av.recomendacion || "", pageWidth - 50);
              recomLines.slice(0, 2).forEach((line, idx) => {
                doc.text(line, 20, yPos + 8 + (idx * 4));
              });
              doc.setTextColor(0, 0, 0);
              yPos += 24;
            }
          } else {
            doc.setFontSize(8);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(100, 100, 100);
            doc.text(vpm?.mensaje || "Datos insuficientes (marca, modelo o valor asegurado faltante)", 15, yPos);
            doc.setTextColor(0, 0, 0);
            yPos += 6;
          }
          
          // Separator between vehicles
          if (vIdx < comparison.precios_mercado.vehiculos_consultados.length - 1) {
            doc.setDrawColor(200, 200, 200);
            doc.line(15, yPos, pageWidth - 15, yPos);
            yPos += 4;
          }
        });
      } else if (comparison.precios_mercado.exito) {
        // Single-vehicle format (legacy)
        const pm = comparison.precios_mercado;
        
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Búsqueda: ${pm.marca || ""} ${pm.modelo || ""} ${pm.año || ""}`, 15, yPos);
        yPos += 5;
        doc.text(`Vehículos encontrados en el mercado: ${pm.vehiculos_encontrados || 0}`, 15, yPos);
        yPos += 5;
        doc.text(`Fuente: ${pm.fuente || "Análisis de mercado"}`, 15, yPos);
        yPos += 8;
        
        const precioColWidths = [45, 45, 45];
        if (getRemainingSpace() < 28) addNewPage();
        
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("PRECIOS EN DÓLARES (US$)", 15, yPos);
        yPos += 6;
        
        doc.setFillColor(37, 99, 235);
        doc.rect(15, yPos - 4, precioColWidths[0], 10, "F");
        doc.setFillColor(22, 163, 74);
        doc.rect(15 + precioColWidths[0], yPos - 4, precioColWidths[1], 10, "F");
        doc.setFillColor(147, 51, 234);
        doc.rect(15 + precioColWidths[0] + precioColWidths[1], yPos - 4, precioColWidths[2], 10, "F");
        
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.text("PRECIO MÍNIMO", 17, yPos + 2);
        doc.text("PRECIO PROMEDIO", 17 + precioColWidths[0], yPos + 2);
        doc.text("PRECIO MÁXIMO", 17 + precioColWidths[0] + precioColWidths[1], yPos + 2);
        yPos += 10;
        
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(`US$ ${pm.precio_minimo?.toLocaleString('es-DO') || "N/A"}`, 17, yPos);
        doc.text(`US$ ${pm.precio_promedio?.toLocaleString('es-DO') || "N/A"}`, 17 + precioColWidths[0], yPos);
        doc.text(`US$ ${pm.precio_maximo?.toLocaleString('es-DO') || "N/A"}`, 17 + precioColWidths[0] + precioColWidths[1], yPos);
        yPos += 12;
        
        if (pm.precio_minimo_dop) {
          if (getRemainingSpace() < 28) addNewPage();
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text("PRECIOS EN PESOS DOMINICANOS (RD$)", 15, yPos);
          yPos += 6;
          
          doc.setFillColor(5, 150, 105);
          doc.rect(15, yPos - 4, precioColWidths[0], 10, "F");
          doc.setFillColor(59, 130, 246);
          doc.rect(15 + precioColWidths[0], yPos - 4, precioColWidths[1], 10, "F");
          doc.setFillColor(168, 85, 247);
          doc.rect(15 + precioColWidths[0] + precioColWidths[1], yPos - 4, precioColWidths[2], 10, "F");
          
          doc.setFontSize(8);
          doc.setTextColor(255, 255, 255);
          doc.text("PRECIO MÍNIMO", 17, yPos + 2);
          doc.text("PRECIO PROMEDIO", 17 + precioColWidths[0], yPos + 2);
          doc.text("PRECIO MÁXIMO", 17 + precioColWidths[0] + precioColWidths[1], yPos + 2);
          yPos += 10;
          
          doc.setTextColor(0, 0, 0);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text(`RD$ ${pm.precio_minimo_dop?.toLocaleString('es-DO') || "N/A"}`, 17, yPos);
          doc.text(`RD$ ${pm.precio_promedio_dop?.toLocaleString('es-DO') || "N/A"}`, 17 + precioColWidths[0], yPos);
          doc.text(`RD$ ${pm.precio_maximo_dop?.toLocaleString('es-DO') || "N/A"}`, 17 + precioColWidths[0] + precioColWidths[1], yPos);
          yPos += 12;
        }
        
        if (pm.analisis_valor) {
          if (getRemainingSpace() < 30) addNewPage();
          const av = pm.analisis_valor;
          let bgColor, textColor, estadoTexto;
          if (av.estado === "DENTRO_DEL_RANGO") {
            bgColor = [220, 252, 231]; textColor = [21, 128, 61]; estadoTexto = "VALOR ASEGURADO ADECUADO";
          } else if (av.estado === "POR_DEBAJO") {
            bgColor = [254, 249, 195]; textColor = [161, 98, 7]; estadoTexto = "VALOR ASEGURADO POR DEBAJO DEL MERCADO";
          } else {
            bgColor = [255, 237, 213]; textColor = [194, 65, 12]; estadoTexto = "VALOR ASEGURADO POR ENCIMA DEL MERCADO";
          }
          doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          doc.rect(15, yPos - 4, pageWidth - 30, 25, "F");
          doc.setTextColor(textColor[0], textColor[1], textColor[2]);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text(estadoTexto, 20, yPos + 2);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(60, 60, 60);
          const recomLines = doc.splitTextToSize(av.recomendacion || "", pageWidth - 50);
          recomLines.forEach((line, idx) => { doc.text(line, 20, yPos + 8 + (idx * 4)); });
          if (av.valor_asegurado) {
            const monedaDisplay = av.moneda_comparacion || (av.moneda_valor_asegurado === 'USD' ? 'US$' : 'RD$');
            doc.text(`Valor Asegurado Actual: ${monedaDisplay} ${av.valor_asegurado.toLocaleString('es-DO')}`, 20, yPos + 18);
          }
          doc.setTextColor(0, 0, 0);
          yPos += 30;
        }
      } else {
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(100, 100, 100);
        doc.text(comparison.precios_mercado.mensaje || "No se pudieron obtener precios de mercado para este vehículo.", 15, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 8;
      }

      // ⇨ Conclusiones del análisis
      if (comparison.precios_mercado.exito || comparison.precios_mercado.vehiculos_consultados?.length) {
        checkNewPage(30);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(25, 57, 102);
        doc.text("Conclusiones del análisis", 15, yPos);
        yPos += 5;
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);

        // Build conclusions from available state
        const conclusiones = [];
        const pmTop = comparison.precios_mercado;
        const vehsList = pmTop.vehiculos_consultados || [];
        if (vehsList.length > 0) {
          let dentro = 0, debajo = 0, encima = 0;
          vehsList.forEach(v => {
            const estado = v.analisis_valor?.estado;
            if (estado === "DENTRO_DEL_RANGO") dentro++;
            else if (estado === "POR_DEBAJO") debajo++;
            else if (estado === "POR_ENCIMA") encima++;
          });
          if (vehsList.length > 1) {
            conclusiones.push(`Se analizaron ${vehsList.length} vehículos: ${dentro} con valor asegurado adecuado, ${debajo} por debajo del rango de mercado y ${encima} por encima.`);
          }
        }
        if (pmTop.analisis_valor) {
          const av = pmTop.analisis_valor;
          if (av.estado === "DENTRO_DEL_RANGO") {
            conclusiones.push("El valor asegurado se encuentra dentro del rango observado en el mercado — cobertura equilibrada frente al riesgo real del vehículo.");
          } else if (av.estado === "POR_DEBAJO") {
            conclusiones.push("El valor asegurado está por debajo del rango de mercado. En caso de pérdida total el cliente podría recibir una indemnización inferior al costo de reposición — se sugiere revisar al alza.");
          } else if (av.estado === "POR_ENCIMA") {
            conclusiones.push("El valor asegurado supera el rango de mercado. Esto incrementa la prima sin beneficio adicional en pérdida total (aplican límites del valor real) — se sugiere ajustar a la baja.");
          }
          if (av.recomendacion) conclusiones.push(av.recomendacion);
        }
        if (pmTop.precio_promedio && pmTop.vehiculos_encontrados) {
          const monedaPrecio = pmTop.moneda_precios === "DOP" ? "RD$" : "US$";
          conclusiones.push(`Precio promedio de referencia del mercado: ${monedaPrecio} ${pmTop.precio_promedio.toLocaleString('es-DO')} (basado en ${pmTop.vehiculos_encontrados} anuncios consultados).`);
        }
        if (conclusiones.length === 0) {
          conclusiones.push("Los datos disponibles son insuficientes para generar conclusiones cuantitativas definitivas — se recomienda al cliente solicitar una tasación formal del vehículo.");
        }
        conclusiones.forEach(c => {
          const cLines = doc.splitTextToSize(`• ${c}`, pageWidth - 35);
          cLines.forEach(line => { checkNewPage(5); doc.text(line, 17, yPos); yPos += 4.5; });
        });
        yPos += 4;
      }

      // ⇨ Disclaimer legal (siempre al final del Análisis de Valor Asegurado)
      checkNewPage(18);
      doc.setFillColor(254, 243, 199); // light amber
      const disclaimer = "Esta información se proporciona únicamente como referencia ilustrativa; no constituye una tasación formal. Recomendamos al cliente evaluar el valor conforme a las condiciones actuales de su vehículo.";
      const discLines = doc.splitTextToSize(disclaimer, pageWidth - 40);
      const discH = discLines.length * 4.5 + 6;
      doc.roundedRect(15, yPos - 3, pageWidth - 30, discH, 2, 2, "F");
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(120, 83, 30);
      discLines.forEach((line, i) => { doc.text(line, 20, yPos + (i * 4.5)); });
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      yPos += discH + 3;

      yPos += 5;
    }

    // Datos Generales de las Pólizas
    if (comparison.datos_generales_polizas?.length > 0) {
      addSectionTitle("DATOS GENERALES DE LAS PÓLIZAS");
      
      doc.setFontSize(8);
      const polA = comparison.datos_generales_polizas[0] || {};
      const polB = comparison.datos_generales_polizas[1] || {};
      
      // Build dynamic rows based on available data
      const dgRows = [
        ["Tipo de Póliza", polA.tipo_poliza || "N/A", polB.tipo_poliza || "N/A"],
        ["Producto / Plan", polA.producto_plan || "N/A", polB.producto_plan || "N/A"],
        ["Aseguradora", polA.aseguradora || "N/A", polB.aseguradora || "N/A"],
        ["Vigencia", `${polA.vigencia_inicio || "N/A"} - ${polA.vigencia_fin || "N/A"}`, `${polB.vigencia_inicio || "N/A"} - ${polB.vigencia_fin || "N/A"}`],
      ];
      if (polA.numero_poliza || polB.numero_poliza) dgRows.splice(0, 0, ["No. Póliza", polA.numero_poliza || "N/A", polB.numero_poliza || "N/A"]);
      if (polA.referencia || polB.referencia) dgRows.push(["Referencia", polA.referencia || "N/A", polB.referencia || "N/A"]);
      if (polA.ramo || polB.ramo) dgRows.push(["Ramo", polA.ramo || "N/A", polB.ramo || "N/A"]);
      if (polA.moneda || polB.moneda) dgRows.push(["Moneda", polA.moneda || "N/A", polB.moneda || "N/A"]);
      if (polA.forma_pago || polB.forma_pago) dgRows.push(["Forma de Pago", polA.forma_pago || "N/A", polB.forma_pago || "N/A"]);
      if (polA.ncf || polB.ncf) dgRows.push(["NCF", polA.ncf || "N/A", polB.ncf || "N/A"]);
      if (polA.tipo_movimiento || polB.tipo_movimiento) dgRows.push(["Tipo Movimiento", polA.tipo_movimiento || "N/A", polB.tipo_movimiento || "N/A"]);
      
      const dgHeaders = ["ÍTEM", "PÓLIZA ACTUAL", "RENOVACIÓN"];
      const dgColWidths = [40, 60, 60];
      const dgTableWidth = dgColWidths.reduce((a, b) => a + b, 0);
      
      const drawDgHeader = () => {
        doc.setFillColor(247, 247, 247);
        doc.rect(15, yPos - 4, dgTableWidth, 8, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        let xPos = 15;
        dgHeaders.forEach((h, i) => {
          doc.text(h, xPos + 2, yPos);
          xPos += dgColWidths[i];
        });
        yPos += 8;
      };
      
      drawDgHeader();
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      dgRows.forEach(row => {
        if (yPos + 10 > pageHeight - PAGE_MARGIN_BOTTOM) {
          addNewPage();
          drawDgHeader();
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
        }
        let xPos = 15;
        row.forEach((cell, i) => {
          const lines = doc.splitTextToSize(cell, dgColWidths[i] - 4);
          doc.text(lines[0], xPos + 2, yPos);
          xPos += dgColWidths[i];
        });
        yPos += 7;
      });
      yPos += 5;
    }

    // ===== RESUMEN (detalle con tarjetas de puntuación, hallazgos y diferencias clave) =====
    if (comparison.informe_ejecutivo) {
      const ie = comparison.informe_ejecutivo;
      
      // Always start on a new page for the detailed summary
      addNewPage();
      
      // Header with accent bar
      doc.setFillColor(0, 74, 153);
      doc.rect(15, yPos - 5, pageWidth - 30, 12, "F");
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("RESUMEN", pageWidth / 2, yPos + 2, { align: "center" });
      doc.setTextColor(0, 0, 0);
      yPos += 15;

      // Resumen General: eliminado por requerimiento del cliente.

      // Puntuaciones y Veredicto  (Puntuación en escala 0-10, NO 0-100)
      if (ie.puntuacion_poliza_a || ie.puntuacion_poliza_b) {
        checkNewPage(36);
        const scoreBoxWidth = (pageWidth - 40) / 3;
        
        // Poliza Actual Score
        doc.setFillColor(37, 99, 235);
        doc.roundedRect(15, yPos - 3, scoreBoxWidth, 24, 2, 2, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.text("POLIZA ACTUAL", 15 + scoreBoxWidth / 2, yPos + 1, { align: "center" });
        doc.setFontSize(14);
        doc.text(`${ie.puntuacion_poliza_a || "?"}/10`, 15 + scoreBoxWidth / 2, yPos + 11, { align: "center" });
        doc.setFontSize(6);
        doc.setFont("helvetica", "normal");
        doc.text("Calificación (escala 0-10)", 15 + scoreBoxWidth / 2, yPos + 17, { align: "center" });
        
        // Renovacion Score
        doc.setFillColor(22, 163, 74);
        doc.roundedRect(15 + scoreBoxWidth + 5, yPos - 3, scoreBoxWidth, 24, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text("RENOVACION", 15 + scoreBoxWidth + 5 + scoreBoxWidth / 2, yPos + 1, { align: "center" });
        doc.setFontSize(14);
        doc.text(`${ie.puntuacion_poliza_b || "?"}/10`, 15 + scoreBoxWidth + 5 + scoreBoxWidth / 2, yPos + 11, { align: "center" });
        doc.setFontSize(6);
        doc.setFont("helvetica", "normal");
        doc.text("Calificación (escala 0-10)", 15 + scoreBoxWidth + 5 + scoreBoxWidth / 2, yPos + 17, { align: "center" });
        
        // Veredicto
        doc.setFillColor(147, 51, 234);
        doc.roundedRect(15 + (scoreBoxWidth + 5) * 2, yPos - 3, scoreBoxWidth, 24, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text("VEREDICTO", 15 + (scoreBoxWidth + 5) * 2 + scoreBoxWidth / 2, yPos + 1, { align: "center" });
        doc.setFontSize(8);
        const vLines = doc.splitTextToSize(ie.veredicto || "N/A", scoreBoxWidth - 6);
        doc.text(vLines[0], 15 + (scoreBoxWidth + 5) * 2 + scoreBoxWidth / 2, yPos + 10, { align: "center" });
        doc.setFontSize(6);
        doc.setFont("helvetica", "normal");
        doc.text("Recomendación final", 15 + (scoreBoxWidth + 5) * 2 + scoreBoxWidth / 2, yPos + 17, { align: "center" });
        
        doc.setTextColor(0, 0, 0);
        yPos += 28;
      }

      // Hallazgos Principales
      if (ie.hallazgos?.length > 0) {
        checkNewPage(25);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 74, 153);
        doc.text("HALLAZGOS PRINCIPALES", 15, yPos);
        yPos += 2;
        doc.setDrawColor(0, 74, 153);
        doc.line(15, yPos, 80, yPos);
        yPos += 6;
        doc.setTextColor(0, 0, 0);

        const impactColors = { alto: [220, 38, 38], medio: [217, 119, 6], bajo: [22, 163, 74] };
        const changeIcons = { aumento: "\u2191", disminucion: "\u2193", nuevo: "+", eliminado: "\u00D7", alerta: "!", sin_cambio: "=" };

        ie.hallazgos.forEach(h => {
          checkNewPage(18);
          
          // Impact badge
          const impColor = impactColors[h.impacto] || [100, 100, 100];
          doc.setFillColor(impColor[0], impColor[1], impColor[2]);
          doc.roundedRect(15, yPos - 3, 6, 6, 1, 1, "F");
          
          // Category + change icon
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          const changeIcon = changeIcons[h.tipo_cambio] || "";
          doc.text(`${changeIcon} ${h.categoria}`, 23, yPos + 1);
          
          // Description
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          const descLines = doc.splitTextToSize(h.descripcion || "", pageWidth - 40);
          descLines.forEach((line, i) => {
            if (i > 0) checkNewPage(5);
            doc.text(line, 23, yPos + 5 + (i * 4));
          });
          yPos += 5 + descLines.length * 4 + 3;
        });
        yPos += 3;
      }

      // Diferencias Clave (solo comparación de primas)
      if (filterDiferenciasPrima(ie.diferencias_clave).length > 0) {
        checkNewPage(25);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 74, 153);
        doc.text("DIFERENCIAS CLAVE", 15, yPos);
        yPos += 2;
        doc.setDrawColor(0, 74, 153);
        doc.line(15, yPos, 70, yPos);
        yPos += 6;
        doc.setTextColor(0, 0, 0);

        filterDiferenciasPrima(ie.diferencias_clave).forEach((diff, i) => {
          checkNewPage(10);
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.text(`${i + 1}.`, 17, yPos);
          doc.setFont("helvetica", "normal");
          const diffLines = doc.splitTextToSize(diff, pageWidth - 40);
          diffLines.forEach((line, li) => {
            doc.text(line, 23, yPos + (li * 4));
          });
          yPos += diffLines.length * 4 + 3;
        });
        yPos += 3;
      }

      // Recomendación Ejecutiva
      if (ie.recomendacion_ejecutiva) {
        checkNewPage(25);
        doc.setFillColor(245, 247, 250);
        const recLines = doc.splitTextToSize(ie.recomendacion_ejecutiva, pageWidth - 44);
        const recHeight = recLines.length * 5 + 12;
        doc.roundedRect(15, yPos - 4, pageWidth - 30, recHeight, 2, 2, "F");
        
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 74, 153);
        doc.text("RECOMENDACION EJECUTIVA:", 20, yPos + 2);
        yPos += 7;
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        recLines.forEach(line => {
          doc.text(line, 20, yPos);
          yPos += 5;
        });
        yPos += 5;
      }
    }

    // Tabla Comparativa - Formato de tabla real
    if (comparison.tabla_comparativa?.length > 0) {
      addSectionTitle("TABLA COMPARATIVA DETALLADA");
      
      // Column widths
      const colWidths = [38, 48, 48, 46]; // item, polA, polB, obs
      const tableWidth = colWidths.reduce((a, b) => a + b, 0);
      const startX = 15;
      const HEADER_HEIGHT = 10;
      
      // Reusable function to draw the table header
      const drawTableHeader = () => {
        doc.setFillColor(100, 116, 139);
        doc.rect(startX, yPos - 4, colWidths[0], HEADER_HEIGHT, "F");
        doc.setFillColor(37, 99, 235);
        doc.rect(startX + colWidths[0], yPos - 4, colWidths[1], HEADER_HEIGHT, "F");
        doc.setFillColor(22, 163, 74);
        doc.rect(startX + colWidths[0] + colWidths[1], yPos - 4, colWidths[2], HEADER_HEIGHT, "F");
        doc.setFillColor(217, 119, 6);
        doc.rect(startX + colWidths[0] + colWidths[1] + colWidths[2], yPos - 4, colWidths[3], HEADER_HEIGHT, "F");
        
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text("ÍTEM EVALUADO", startX + 2, yPos + 2);
        doc.text("PÓLIZA ACTUAL", startX + colWidths[0] + 2, yPos + 2);
        doc.text("RENOVACIÓN", startX + colWidths[0] + colWidths[1] + 2, yPos + 2);
        doc.text("OBSERVACIONES", startX + colWidths[0] + colWidths[1] + colWidths[2] + 2, yPos + 2);
        
        doc.setTextColor(0, 0, 0);
        yPos += HEADER_HEIGHT;
      };
      
      // Draw initial header
      drawTableHeader();
      
      // Table rows
      doc.setFontSize(6);
      // Helper to draw a single table row
      const drawTableRow = (row, idx) => {
        const itemLines = doc.splitTextToSize(row.item || "", colWidths[0] - 4);
        const polALines = doc.splitTextToSize(isMeaningfulValue(row.poliza_a) ? row.poliza_a : "—", colWidths[1] - 4);
        const polBLines = doc.splitTextToSize(isMeaningfulValue(row.poliza_b) ? row.poliza_b : "—", colWidths[2] - 4);
        const obsLines = doc.splitTextToSize(row.observacion || "-", colWidths[3] - 4);
        const maxLines = Math.max(itemLines.length, polALines.length, polBLines.length, obsLines.length);
        const rowHeight = Math.max(8, maxLines * 4 + 6);
        
        if (yPos + rowHeight > pageHeight - PAGE_MARGIN_BOTTOM) {
          addNewPage();
          drawTableHeader();
          doc.setFontSize(6);
        }
        
        doc.setFillColor(255, 255, 255);
        doc.rect(startX, yPos - 3, tableWidth, rowHeight, "F");
        
        if (idx % 2 === 1) {
          doc.setFillColor(245, 245, 245);
          doc.rect(startX, yPos - 3, tableWidth, rowHeight, "F");
        }
        
        doc.setDrawColor(180, 180, 180);
        let xPos = startX;
        colWidths.forEach(w => {
          doc.rect(xPos, yPos - 3, w, rowHeight, "S");
          xPos += w;
        });
        
        let cellY = yPos;
        
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        itemLines.forEach((line, i) => {
          doc.text(line, startX + 2, cellY + (i * 4));
        });
        
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 65, 85);
        polALines.forEach((line, i) => {
          doc.text(line, startX + colWidths[0] + 2, cellY + (i * 4));
        });
        
        polBLines.forEach((line, i) => {
          doc.text(line, startX + colWidths[0] + colWidths[1] + 2, cellY + (i * 4));
        });
        
        doc.setFont("helvetica", "italic");
        doc.setTextColor(146, 64, 14);
        obsLines.forEach((line, i) => {
          doc.text(line, startX + colWidths[0] + colWidths[1] + colWidths[2] + 2, cellY + (i * 4));
        });
        
        doc.setTextColor(0, 0, 0);
        yPos += rowHeight;
      };

      filterTablaComparativa(comparison.tabla_comparativa).forEach((row, idx) => {
        drawTableRow(row, idx);
      });

      // Append coberturas as additional rows in the same table
      if (comparison.detalle_coberturas?.length > 0) {
        // Section separator row for COBERTURAS
        const sepHeight = 8;
        if (yPos + sepHeight > pageHeight - PAGE_MARGIN_BOTTOM) {
          addNewPage();
          drawTableHeader();
          doc.setFontSize(6);
        }
        doc.setFillColor(30, 58, 95);
        doc.rect(startX, yPos - 3, tableWidth, sepHeight, "F");
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text("COBERTURAS COMPARADAS", startX + tableWidth / 2, yPos + 1, { align: "center" });
        doc.setTextColor(0, 0, 0);
        yPos += sepHeight;
        doc.setFontSize(6);

        const baseIdx = filterTablaComparativa(comparison.tabla_comparativa).length;
        comparison.detalle_coberturas.forEach((cob, idx) => {
          const polAText = [
            isMeaningfulValue(cob.poliza_a_limite) ? `Límite: ${cob.poliza_a_limite}` : null,
            isMeaningfulValue(cob.poliza_a_deducible) ? `Deducible: ${cob.poliza_a_deducible}` : null,
            isMeaningfulValue(cob.poliza_a_prima) ? `Prima: ${cob.poliza_a_prima}` : null,
          ].filter(Boolean).join("\n") || "N/A";
          
          const polBText = [
            cob.poliza_b_limite ? `Límite: ${cob.poliza_b_limite}` : null,
            isMeaningfulValue(cob.poliza_b_deducible) ? `Deducible: ${cob.poliza_b_deducible}` : null,
            isMeaningfulValue(cob.poliza_b_prima) ? `Prima: ${cob.poliza_b_prima}` : null,
          ].filter(Boolean).join("\n") || "N/A";

          let obs = compareCoverageObservation(cob);

          drawTableRow({
            item: cob.cobertura || "Cobertura",
            poliza_a: polAText,
            poliza_b: polBText,
            observacion: obs
          }, baseIdx + idx);
        });
      }

      yPos += 8;
    }

    // Detalle de Coberturas (sección dedicada): eliminada por requerimiento del cliente.
    if (false && comparison.detalle_coberturas?.length > 0) {
      addSectionTitle("DETALLE DE COBERTURAS - LÍMITES, DEDUCIBLES Y PRIMAS", 50);
      
      const dcColWidths = [30, 25, 25, 25, 25, 25, 25];
      const dcTableWidth = dcColWidths.reduce((a, b) => a + b, 0);
      const dcStartX = 15;
      const DC_HEADER_HEIGHT = 20;
      
      const drawDcHeader = () => {
        const row1H = 10;
        const row2H = 10;
        const polAColor = [37, 99, 235];
        const polBColor = [22, 163, 74];
        const polASubColor = [30, 64, 175];
        const polBSubColor = [21, 128, 61];
        
        // COBERTURA cell - spans full height
        doc.setFillColor(30, 58, 95);
        doc.rect(dcStartX, yPos - 4, dcColWidths[0], row1H + row2H, "F");
        
        // Row 1: PÓLIZA ACTUAL header
        doc.setFillColor(polAColor[0], polAColor[1], polAColor[2]);
        doc.rect(dcStartX + dcColWidths[0], yPos - 4, dcColWidths[1] + dcColWidths[2] + dcColWidths[3], row1H, "F");
        
        // Row 1: RENOVACIÓN header
        doc.setFillColor(polBColor[0], polBColor[1], polBColor[2]);
        doc.rect(dcStartX + dcColWidths[0] + dcColWidths[1] + dcColWidths[2] + dcColWidths[3], yPos - 4, dcColWidths[4] + dcColWidths[5] + dcColWidths[6], row1H, "F");
        
        // Row 1 text
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        doc.text("COBERTURA", dcStartX + 2, yPos + 3);
        doc.text("PÓLIZA ACTUAL", dcStartX + dcColWidths[0] + 18, yPos + 3);
        doc.text("RENOVACIÓN", dcStartX + dcColWidths[0] + dcColWidths[1] + dcColWidths[2] + dcColWidths[3] + 18, yPos + 3);
        
        // Row 2: Sub-headers
        const subY = yPos - 4 + row1H;
        const subLabels = ["Límite", "Deducible", "Prima"];
        
        // Póliza Actual: 3 sub-header cells
        const polAStartX = dcStartX + dcColWidths[0];
        for (let i = 0; i < 3; i++) {
          const cx = polAStartX + (i * 25);
          doc.setFillColor(polASubColor[0], polASubColor[1], polASubColor[2]);
          doc.rect(cx, subY, dcColWidths[1 + i], row2H, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(6);
          doc.setTextColor(255, 255, 255);
          doc.text(subLabels[i], cx + 2, subY + 7);
        }
        
        // Renovación: 3 sub-header cells
        const polBStartX = polAStartX + dcColWidths[1] + dcColWidths[2] + dcColWidths[3];
        for (let i = 0; i < 3; i++) {
          const cx = polBStartX + (i * 25);
          doc.setFillColor(polBSubColor[0], polBSubColor[1], polBSubColor[2]);
          doc.rect(cx, subY, dcColWidths[4 + i], row2H, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(6);
          doc.setTextColor(255, 255, 255);
          doc.text(subLabels[i], cx + 2, subY + 7);
        }
        
        doc.setTextColor(0, 0, 0);
        yPos += DC_HEADER_HEIGHT;
      };
      
      drawDcHeader();
      
      doc.setFontSize(5.5);
      comparison.detalle_coberturas.forEach((cob, idx) => {
        const cobName = doc.splitTextToSize(cob.cobertura || "", dcColWidths[0] - 3);
        
        // Calculate lines for ALL cells to get the true max height
        const cells = [
          cob.poliza_a_limite || "-", cob.poliza_a_deducible || "-", cob.poliza_a_prima || "-",
          cob.poliza_b_limite || "-", cob.poliza_b_deducible || "-", cob.poliza_b_prima || "-"
        ];
        const cellLinesCounts = cells.map((val, ci) => {
          return doc.splitTextToSize(val, dcColWidths[ci + 1] - 3).length;
        });
        const maxCellLines = Math.max(cobName.length, ...cellLinesCounts);
        const rowHeight = Math.max(8, maxCellLines * 3.5 + 5);
        
        if (yPos + rowHeight > pageHeight - PAGE_MARGIN_BOTTOM) {
          addNewPage();
          drawDcHeader();
          doc.setFontSize(5.5);
        }
        
        // Background
        if (idx % 2 === 1) {
          doc.setFillColor(248, 250, 252);
          doc.rect(dcStartX, yPos - 3, dcTableWidth, rowHeight, "F");
        } else {
          doc.setFillColor(255, 255, 255);
          doc.rect(dcStartX, yPos - 3, dcTableWidth, rowHeight, "F");
        }
        
        // Border
        doc.setDrawColor(220, 220, 220);
        let borderX = dcStartX;
        dcColWidths.forEach(w => {
          doc.rect(borderX, yPos - 3, w, rowHeight, "S");
          borderX += w;
        });
        
        // Cobertura name
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        cobName.forEach((line, i) => { doc.text(line, dcStartX + 1, yPos + (i * 3.5)); });
        
        // Cell values
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 65, 85);
        let cellX = dcStartX + dcColWidths[0];
        cells.forEach((val, ci) => {
          const cellLines = doc.splitTextToSize(val, dcColWidths[ci + 1] - 3);
          cellLines.forEach((line, li) => { doc.text(line, cellX + 1, yPos + (li * 3.5)); });
          cellX += dcColWidths[ci + 1];
        });
        
        doc.setTextColor(0, 0, 0);
        yPos += rowHeight;
      });
      yPos += 8;
    }

    // Escenarios Críticos
    if (comparison.escenarios_criticos?.length > 0) {
      addSectionTitle("ANÁLISIS DE ESCENARIOS CRÍTICOS", 50);
      
      comparison.escenarios_criticos.forEach((escenario, idx) => {
        // Ensure scenario title + at least some description stay together
        const minScenarioBlock = 30;
        if (getRemainingSpace() < minScenarioBlock) {
          addNewPage();
        }
        
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`Escenario ${idx + 1}: ${escenario.nombre}`, 15, yPos);
        yPos += 6;
        
        if (escenario.descripcion) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          addText(escenario.descripcion, 15, 8);
          yPos += 3;
        }
        
        if (escenario.flujo_reclamacion?.length > 0) {
          doc.setFontSize(7);
          escenario.flujo_reclamacion.forEach(flujo => {
            // Calculate space needed for concept + A/B values
            const conceptLines = doc.splitTextToSize(flujo.concepto || "", pageWidth - 30);
            const abText = `A: ${flujo.poliza_a || "N/A"}  |  B: ${flujo.poliza_b || "N/A"}`;
            const abLines = doc.splitTextToSize(abText, pageWidth - 35);
            const neededSpace = (conceptLines.length * 4) + (abLines.length * 4) + 6;
            checkNewPage(neededSpace);
            doc.setFont("helvetica", "bold");
            conceptLines.forEach((line, i) => {
              doc.text(line, 20, yPos + (i * 4));
            });
            yPos += conceptLines.length * 4;
            doc.setFont("helvetica", "normal");
            abLines.forEach((line, i) => {
              doc.text(line, 25, yPos + (i * 4));
            });
            yPos += abLines.length * 4 + 2;
          });
        }
        
        if (escenario.analisis_impacto) {
          // Calculate full height of analisis_impacto block
          doc.setFontSize(8);
          const aiLines = doc.splitTextToSize(escenario.analisis_impacto, pageWidth - 30);
          const aiHeight = 8 + (aiLines.length * 5);
          // If the entire block doesn't fit, start on a new page
          if (getRemainingSpace() < Math.min(aiHeight, 60)) {
            addNewPage();
          }
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.text("Análisis de Impacto:", 15, yPos);
          yPos += 5;
          doc.setFont("helvetica", "normal");
          addText(escenario.analisis_impacto, 15, 8);
        }
        yPos += 8;
      });
    }

    // Historial: recomendaciones de comparaciones anteriores del mismo cliente/póliza
    if (comparison.historial_recomendaciones) {
      const hr = comparison.historial_recomendaciones;
      addSectionTitle("HISTORIAL: COMPARACIÓN ANTERIOR DEL MISMO CLIENTE/PÓLIZA", 40);

      const fechaAnt = hr.fecha_anterior || String(comparison.historial_match?.fecha || "").slice(0, 10);
      if (fechaAnt) {
        if (getRemainingSpace() < 15) addNewPage();
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text(`Fecha de la comparación anterior: ${fechaAnt}`, 15, yPos);
        yPos += 6;
      }

      if (hr.recomendaciones_vigentes?.length > 0) {
        if (getRemainingSpace() < 20) addNewPage();
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(180, 120, 0);
        doc.text("Recomendaciones anteriores que SIGUEN vigentes:", 15, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 5;
        doc.setFont("helvetica", "normal");
        hr.recomendaciones_vigentes.forEach((rec) => {
          const lines = doc.splitTextToSize(`• ${rec}`, pageWidth - 35);
          checkNewPage(lines.length * 4 + 3);
          doc.setFontSize(8);
          lines.forEach((line, i) => doc.text(line, 18, yPos + (i * 4)));
          yPos += lines.length * 4 + 2;
        });
        yPos += 3;
      }

      if (hr.cambios_desde_ultima?.length > 0) {
        if (getRemainingSpace() < 20) addNewPage();
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("Cambios desde la última comparación:", 15, yPos);
        yPos += 5;
        doc.setFont("helvetica", "normal");
        hr.cambios_desde_ultima.forEach((cam) => {
          const lines = doc.splitTextToSize(`• ${cam}`, pageWidth - 35);
          checkNewPage(lines.length * 4 + 3);
          doc.setFontSize(8);
          lines.forEach((line, i) => doc.text(line, 18, yPos + (i * 4)));
          yPos += lines.length * 4 + 2;
        });
        yPos += 3;
      }

      if (hr.nota) {
        if (getRemainingSpace() < 20) addNewPage();
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        addText(hr.nota, 15, 8);
        doc.setFont("helvetica", "normal");
      }
      yPos += 5;
    }

    // Dictamen Pericial
    if (comparison.dictamen_pericial) {
      addSectionTitle("DICTAMEN PERICIAL Y RECOMENDACIÓN ESTRATÉGICA", 50);
      
      const dp = comparison.dictamen_pericial;
      
      if (dp.alerta_fundamental) {
        if (getRemainingSpace() < 25) addNewPage();
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(192, 57, 43);
        doc.text("ALERTA FUNDAMENTAL:", 15, yPos);
        yPos += 5;
        doc.setFont("helvetica", "normal");
        addText(dp.alerta_fundamental, 15, 8);
        doc.setTextColor(0, 0, 0);
        yPos += 3;
      }
      
      if (dp.hipotesis_estrategica) {
        if (getRemainingSpace() < 25) addNewPage();
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("Hipótesis Estratégica:", 15, yPos);
        yPos += 5;
        doc.setFont("helvetica", "normal");
        addText(dp.hipotesis_estrategica, 15, 8);
        yPos += 5;
      }
      
      if (dp.calificaciones?.length > 0) {
        if (getRemainingSpace() < 30) addNewPage();
        doc.setFontSize(8);
        dp.calificaciones.forEach(cal => {
          // Keep criterio + its values together
          if (getRemainingSpace() < 20) addNewPage();
          doc.setFont("helvetica", "bold");
          doc.text(`${cal.criterio}:`, 15, yPos);
          doc.setFont("helvetica", "normal");
          doc.text(`A: ${cal.poliza_a}  |  B: ${cal.poliza_b}`, 70, yPos);
          yPos += 4;
          if (cal.justificacion) {
            doc.setFont("helvetica", "italic");
            doc.setTextColor(100, 100, 100);
            const justLines = doc.splitTextToSize(cal.justificacion, pageWidth - 35);
            justLines.forEach(line => {
              checkNewPage(5);
              doc.text(line, 20, yPos);
              yPos += 4;
            });
            doc.setTextColor(0, 0, 0);
          }
          yPos += 2;
        });
        yPos += 3;
      }
      
      // Análisis Detallado: eliminado por requerimiento del cliente.

      if (dp.recomendacion_final) {
        if (getRemainingSpace() < 25) addNewPage();
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 100, 0);
        doc.text("Recomendación Final:", 15, yPos);
        yPos += 5;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        addText(dp.recomendacion_final, 15, 8);
        yPos += 5;
      }
    }

    // Comunicación con Aseguradora
    if (comparison.comunicacion_aseguradora?.cuerpo) {
      addSectionTitle("COMUNICACIÓN CON LA ASEGURADORA", 30);
      
      const ca = comparison.comunicacion_aseguradora;
      doc.setFontSize(8);
      if (ca.asunto) {
        doc.setFont("helvetica", "bold");
        doc.text(`Asunto: ${ca.asunto}`, 15, yPos);
        yPos += 6;
      }
      doc.setFont("helvetica", "normal");
      addText(ca.cuerpo, 15, 8);
      yPos += 5;
    }

    // Comunicación con Cliente
    if (comparison.comunicacion_cliente?.cuerpo) {
      addSectionTitle("COMUNICACIÓN CON EL CLIENTE", 30);
      
      const cc = comparison.comunicacion_cliente;
      doc.setFontSize(8);
      if (cc.saludo) {
        doc.setFont("helvetica", "normal");
        doc.text(cc.saludo, 15, yPos);
        yPos += 6;
      }
      addText(cc.cuerpo, 15, 8);
      
      if (cc.datos_propuesta) {
        yPos += 3;
        if (getRemainingSpace() < 30) addNewPage();
        doc.setFont("helvetica", "bold");
        doc.text("Propuesta económica:", 15, yPos);
        yPos += 5;
        doc.setFont("helvetica", "normal");
        const dprop = cc.datos_propuesta;
        if (dprop.monto_asegurado) { checkNewPage(5); doc.text(`Monto Asegurado: ${dprop.monto_asegurado}`, 20, yPos); yPos += 4; }
        if (dprop.prima_neta) { checkNewPage(5); doc.text(`Prima Neta: ${dprop.prima_neta}`, 20, yPos); yPos += 4; }
        if (dprop.impuestos) { checkNewPage(5); doc.text(`Impuestos: ${dprop.impuestos}`, 20, yPos); yPos += 4; }
        if (dprop.prima_total) { checkNewPage(5); doc.text(`Prima Total: ${dprop.prima_total}`, 20, yPos); yPos += 4; }
      }
      yPos += 5;
    }

    // Nota del Asesor
    if (comparison.nota_asesor) {
      if (getRemainingSpace() < 25) addNewPage();
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text("Nota del Asesor (QAsesores SRL):", 15, yPos);
      yPos += 5;
      doc.setFont("helvetica", "italic");
      addText(comparison.nota_asesor, 15, 8);
    }

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      doc.text(
        `QAsesores SRL - Análisis Comparativo de Pólizas - Página ${i} de ${pageCount}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: "center" }
      );
    }
    
    // Apply corporate branding (header+footer) to every page as the last step
    applyBranding();
    
    return doc;
  };

  const generateLegacyPDF = () => {
    if (!comparison) return null;
    
    const doc = patchJsPdfText(new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" }));
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;
    
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Informe de Comparación de Pólizas", pageWidth / 2, yPos, { align: "center" });
    yPos += 15;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Fecha: ${new Date().toLocaleDateString('es-ES')}`, pageWidth / 2, yPos, { align: "center" });
    yPos += 20;
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Documentos Comparados:", 20, yPos);
    yPos += 8;
    doc.setFont("helvetica", "normal");
    doc.text(`• Póliza Actual: ${comparison.document1_name}`, 25, yPos);
    yPos += 6;
    doc.text(`• Renovación: ${comparison.document2_name}`, 25, yPos);
    yPos += 15;
    
    if (comparison.summary) {
      doc.setFont("helvetica", "bold");
      doc.text("Resumen Ejecutivo:", 20, yPos);
      yPos += 8;
      doc.setFont("helvetica", "normal");
      
      const summaryLines = doc.splitTextToSize(comparison.summary, pageWidth - 40);
      summaryLines.forEach(line => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
      yPos += 10;
    }

    if (comparison.differences?.length > 0) {
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Diferencias Identificadas:", 20, yPos);
      yPos += 10;
      
      comparison.differences.forEach((diff, index) => {
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(`${index + 1}. ${diff.aspect || 'Aspecto'}`, 20, yPos);
        if (diff.importance) {
          doc.setFontSize(9);
          doc.text(`[${diff.importance.toUpperCase()}]`, pageWidth - 40, yPos);
        }
        yPos += 7;
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const doc1Text = doc.splitTextToSize(`Póliza Actual: ${diff.document1 || 'N/A'}`, pageWidth - 50);
        doc1Text.forEach(line => {
          doc.text(line, 25, yPos);
          yPos += 5;
        });
        
        const doc2Text = doc.splitTextToSize(`Renovación: ${diff.document2 || 'N/A'}`, pageWidth - 50);
        doc2Text.forEach(line => {
          doc.text(line, 25, yPos);
          yPos += 5;
        });
        
        yPos += 8;
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `QAsesores - Página ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }
    
    return doc;
  };

  const [generatingInforme, setGeneratingInforme] = useState(false);

  const generateInformeEjecutivo = async (force = false) => {
    if (!comparison) return comparison;
    if (!force && comparison.informe_ejecutivo) return comparison;
    try {
      setGeneratingInforme(true);
      toast.info(force ? "Regenerando informe ejecutivo con IA..." : "Generando informe ejecutivo con IA...");
      const url = `${API}/comparisons/${id}/informe-ejecutivo${force ? "?force=true" : ""}`;
      const res = await axios.post(url, {}, getAuthHeaders());
      const updated = { ...comparison, informe_ejecutivo: res.data.informe_ejecutivo };
      setComparison(updated);
      if (force) toast.success("Informe ejecutivo regenerado");
      return updated;
    } catch (err) {
      console.error("Error generating informe ejecutivo:", err);
      toast.error(err.response?.data?.detail || "Error generando informe ejecutivo");
      return comparison;
    } finally {
      setGeneratingInforme(false);
    }
  };

  const downloadPDF = async () => {
    // Generate informe ejecutivo if not yet available
    let comp = comparison;
    if (!comp.informe_ejecutivo) {
      comp = await generateInformeEjecutivo();
    }
    const doc = isDetailedFormat ? generateDetailedPDF(comp) : generateLegacyPDF();
    if (doc) {
      doc.save(`analisis-comparativo-polizas-${id.slice(0, 8)}.pdf`);
      toast.success("PDF descargado exitosamente");
    }
  };

  const openPreviewAndDownload = () => {
    setShowPreview(true);
  };

  const getImportanceBadge = (importance) => {
    switch (importance?.toLowerCase()) {
      case "alta":
      case "high":
        return <Badge variant="destructive" className="bg-orange-100 text-orange-800 border-0">Alta</Badge>;
      case "media":
      case "medium":
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-0">Media</Badge>;
      case "baja":
      case "low":
        return <Badge variant="outline" className="bg-slate-100 text-slate-600 border-0">Baja</Badge>;
      default:
        return null;
    }
  };

  const getCalificacionBadge = (cal) => {
    if (cal?.includes("🟢") || cal?.toLowerCase().includes("superior")) {
      return <Badge className="bg-green-100 text-green-800 border-0">{cal}</Badge>;
    } else if (cal?.includes("🟡") || cal?.toLowerCase().includes("regular")) {
      return <Badge className="bg-yellow-100 text-yellow-800 border-0">{cal}</Badge>;
    } else if (cal?.includes("🔴") || cal?.toLowerCase().includes("inferior") || cal?.toLowerCase().includes("alto")) {
      return <Badge className="bg-red-100 text-red-800 border-0">{cal}</Badge>;
    }
    return <Badge variant="outline">{cal}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">Comparación no encontrada</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <ClienteAtencionWatcher nombre={comparison?.datos_asegurado?.nombre_razon_social} />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="comparison-result-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              data-testid="volver-a-comparacion-btn"
              onClick={() => navigate("/compare")}
              className="mb-2 -ml-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver a Comparación
            </Button>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Análisis Comparativo de Pólizas
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {comparison.document1_name} vs {comparison.document2_name}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={openPreviewAndDownload}
              disabled={comparison.status !== "completed"}
              className="border-slate-200"
              data-testid="preview-report-button"
            >
              <Eye className="w-4 h-4 mr-2" />
              Vista Previa
            </Button>
            <Button
              variant="outline"
              onClick={handleReanalizarConIA}
              disabled={reanalizando || comparison.status === "processing"}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="reanalyze-ia-button"
              title="Vuelve a procesar los documentos originales con la IA. Útil tras mejoras del extractor."
            >
              {reanalizando || comparison.status === "processing" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCw className="w-4 h-4 mr-2" />
              )}
              {comparison.status === "processing" ? "Re-analizando..." : "Re-analizar con IA"}
            </Button>
            <Button
              variant="outline"
              onClick={handleReprocesarDeterministico}
              disabled={reprocessing || comparison.status === "processing"}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="reprocess-deterministic-button"
              title="Limpia datos legacy aplicando el parser determinístico (regex exacto) sobre el texto ya extraído. Rápido, sin LLM."
            >
              {reprocessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCw className="w-4 h-4 mr-2" />
              )}
              {reprocessing ? "Re-procesando..." : "Re-procesar Coberturas"}
            </Button>
            <Button
              variant="outline"
              onClick={downloadPDF}
              disabled={comparison.status !== "completed" || generatingInforme}
              className="border-slate-200"
              data-testid="download-pdf-button"
            >
              {generatingInforme ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {generatingInforme ? "Generando informe..." : "Descargar PDF"}
            </Button>
            <Button
              onClick={openGenerateDialog}
              disabled={comparison.status !== "completed" || generating}
              className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              data-testid="generate-report-button"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Guardar Informe
            </Button>
          </div>
        </div>

        {/* Status Banners */}
        {comparison.status === "processing" && (
          <Card className="border-blue-200 bg-blue-50 mb-6" data-testid="processing-banner">
            <CardContent className="flex items-center gap-4 py-4">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              <div className="flex-1">
                <p className="font-medium text-blue-900">Analizando documentos...</p>
                <p className="text-sm text-blue-700">
                  La comparación con IA está en proceso. Para documentos pequeños toma 1-2 minutos, para documentos grandes con múltiples vehículos puede tomar hasta 5 minutos.
                  {pollCount > 0 && (
                    <span className="ml-2 text-blue-500">
                      (Verificando... {Math.floor(pollCount * 3 / 60)}:{String(pollCount * 3 % 60).padStart(2, '0')})
                    </span>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {comparison.status === "error" && (
          <Card className="border-red-200 bg-red-50 mb-6" data-testid="error-banner">
            <CardContent className="flex items-center gap-4 py-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <div className="flex-1">
                <p className="font-medium text-red-900">Error en el análisis</p>
                <p className="text-sm text-red-700">
                  {comparison.error || "Hubo un problema al analizar los documentos. Puede intentar nuevamente."}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="border-red-200 text-red-700 hover:bg-red-100"
                  data-testid="go-back-button"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {comparison.status === "completed" && Array.isArray(comparison.modelos_reconocidos) && comparison.modelos_reconocidos.length > 0 && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 px-4 py-3" data-testid="modelos-reconocidos-banner">
            <BookOpenCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                Modelo(s) de aseguradora reconocido(s) (Aprendizaje IA)
              </p>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {comparison.modelos_reconocidos.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-700 rounded-full px-2.5 py-1 text-slate-700 dark:text-slate-200" data-testid={`modelo-reconocido-${i}`}>
                    <Building2 className="w-3 h-3 text-emerald-600" />
                    {m.aseguradora} · {m.ramo}
                  </span>
                ))}
              </div>
              <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80 mt-1">
                La IA usó la terminología de estos modelos para una lectura y comparación más precisas.
              </p>
            </div>
          </div>
        )}

        {comparison.status === "completed" && isDetailedFormat && (
          <Tabs defaultValue="resumen" className="space-y-6">
            <TabsList className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1 flex-wrap">
              <TabsTrigger value="resumen">Resumen</TabsTrigger>
              <TabsTrigger value="ejecutivo" data-testid="tab-ejecutivo">Informe Ejecutivo</TabsTrigger>
              <TabsTrigger value="datos">Datos Generales</TabsTrigger>
              <TabsTrigger value="comparativa">Tabla Comparativa</TabsTrigger>
              {/* Pestaña Coberturas eliminada por requerimiento del cliente */}
              <TabsTrigger value="escenarios">Escenarios</TabsTrigger>
              <TabsTrigger value="dictamen">Dictamen</TabsTrigger>
              <TabsTrigger value="comunicacion">Comunicación</TabsTrigger>
            </TabsList>

            {/* Resumen Tab - Datos del Asegurado */}
            <TabsContent value="resumen">
              <div className="space-y-6">
                {/* Datos del Asegurado */}
                {comparison.datos_asegurado && (
                  <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                    <CardHeader className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                      <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-600" />
                        Datos del Asegurado
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Nombre / Razón Social</p>
                            <p className="font-medium text-slate-900 dark:text-white">{comparison.datos_asegurado.nombre_razon_social || "No especificado"}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Identificación (RNC)</p>
                            <p className="font-medium text-slate-900 dark:text-white">{comparison.datos_asegurado.identificacion_rnc || "No especificado"}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 md:col-span-2">
                          <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Dirección</p>
                            <p className="font-medium text-slate-900 dark:text-white">{comparison.datos_asegurado.direccion || "No especificado"}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Datos del Vehículo - solo si es un vehículo individual, no flotilla */}
                {comparison.datos_vehiculo?.es_poliza_vehiculo && !((comparison.precios_mercado?.total_vehiculos || 0) > 1 || (comparison.precios_mercado?.vehiculos_consultados?.length || 0) > 1) && (
                  <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                    <CardHeader className="border-b border-slate-100 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20">
                      <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <Car className="w-5 h-5 text-blue-500" />
                        Datos del Vehículo
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Marca</p>
                          <p className="font-semibold text-slate-900 dark:text-white">{comparison.datos_vehiculo.marca || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Modelo</p>
                          <p className="font-semibold text-slate-900 dark:text-white">{comparison.datos_vehiculo.modelo || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Año</p>
                          <p className="font-semibold text-slate-900 dark:text-white">{comparison.datos_vehiculo.año || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Versión/Submodelo</p>
                          <p className="font-semibold text-slate-900 dark:text-white">{comparison.datos_vehiculo.submodelo || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Color</p>
                          <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.color || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Placa</p>
                          <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.placa || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Chasis</p>
                          <p className="font-medium text-slate-700 dark:text-slate-300 text-sm">{comparison.datos_vehiculo.chasis || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Valor Asegurado</p>
                          <p className="font-bold text-blue-600 dark:text-blue-400">
                            {comparison.datos_vehiculo.valor_asegurado 
                              ? `${comparison.datos_vehiculo.moneda_valor_asegurado || comparison.datos_vehiculo.moneda_valor_asegurado_normalizada || 'RD$'} ${comparison.datos_vehiculo.valor_asegurado.toLocaleString('es-DO')}`
                              : "N/A"
                            }
                          </p>
                        </div>
                        {comparison.datos_vehiculo.tipo_vehiculo && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Tipo Vehículo</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.tipo_vehiculo}</p>
                          </div>
                        )}
                        {comparison.datos_vehiculo.cilindraje && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Cilindraje</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.cilindraje}</p>
                          </div>
                        )}
                        {comparison.datos_vehiculo.cantidad_pasajeros > 0 && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Cant. Pasajeros</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.cantidad_pasajeros}</p>
                          </div>
                        )}
                        {comparison.datos_vehiculo.numero_motor && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">No. Motor</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300 text-sm">{comparison.datos_vehiculo.numero_motor}</p>
                          </div>
                        )}
                        {comparison.datos_vehiculo.tipo_combustible && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Combustible</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.tipo_combustible}</p>
                          </div>
                        )}
                        {comparison.datos_vehiculo.tipo_traccion && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Tracción</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.tipo_traccion}</p>
                          </div>
                        )}
                        {comparison.datos_vehiculo.uso && (
                          <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Uso</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.uso}</p>
                          </div>
                        )}
                        {comparison.datos_vehiculo.domicilio_guarda && (
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500 dark:text-slate-400">Domicilio de Guarda</p>
                            <p className="font-medium text-slate-700 dark:text-slate-300">{comparison.datos_vehiculo.domicilio_guarda}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Precios de Mercado - Soporte para múltiples vehículos */}
                {comparison.precios_mercado && (
                  <Card className="border-2 shadow-sm dark:bg-slate-800 border-purple-200 dark:border-purple-800">
                    <CardHeader className="bg-purple-50 dark:bg-purple-900/20 border-b border-purple-100 dark:border-purple-800">
                      <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                        <BarChart3 className="w-5 h-5 text-purple-500" />
                        Análisis de Valor de Mercado
                        {comparison.precios_mercado.total_vehiculos > 1 && (
                          <Badge variant="outline" className="ml-2 bg-purple-100 text-purple-700" data-testid="market-total-vehicles-badge">
                            {comparison.precios_mercado.total_vehiculos} Vehículos
                          </Badge>
                        )}
                        <Badge variant="outline" className="ml-auto text-xs bg-white dark:bg-slate-700" data-testid="market-source-badge">
                          Fuente: {comparison.precios_mercado.fuente_datos || "Supercarros.com.do"}
                          {typeof comparison.precios_mercado.total_vehiculos_analizados === 'number' && comparison.precios_mercado.total_vehiculos_analizados > 0 && (
                            <span className="ml-1 text-purple-700 dark:text-purple-300">
                              · {comparison.precios_mercado.total_vehiculos_analizados} anuncio{comparison.precios_mercado.total_vehiculos_analizados === 1 ? '' : 's'}
                            </span>
                          )}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="flex items-center justify-between gap-2 flex-wrap">
                        <span>Comparación del valor asegurado con precios actuales del mercado</span>
                        <span className="flex items-center gap-3 flex-wrap">
                          {comparison.precios_mercado.fecha_consulta && (
                            <span className="text-xs italic text-slate-500 dark:text-slate-400" data-testid="market-query-date">
                              Consulta: {(() => {
                                try {
                                  return new Date(comparison.precios_mercado.fecha_consulta).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' });
                                } catch (e) {
                                  return comparison.precios_mercado.fecha_consulta;
                                }
                              })()}
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleReconsultarPrecios}
                            disabled={reconsultando}
                            data-testid="reconsultar-precios-btn"
                            className="h-7 text-xs border-purple-300 hover:bg-purple-100 dark:border-purple-700 dark:hover:bg-purple-900/40"
                          >
                            {reconsultando ? (
                              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Consultando…</>
                            ) : (
                              <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-consultar precios</>
                            )}
                          </Button>
                        </span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {/* Tasa de cambio global */}
                      {comparison.precios_mercado.tasa_cambio && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/30 p-2 rounded mb-4">
                          <DollarSign className="w-4 h-4" />
                          <span>Tasa de cambio: <span className="font-semibold">1 US$ = RD$ {comparison.precios_mercado.tasa_cambio}</span></span>
                          <span className="text-xs">({comparison.precios_mercado.fuente_tasa_cambio || "BCRD"})</span>
                        </div>
                      )}

                      {/* Lista de vehículos consultados */}
                      {comparison.precios_mercado.vehiculos_consultados && comparison.precios_mercado.vehiculos_consultados.length > 0 ? (
                        <div className="space-y-6">
                          {comparison.precios_mercado.vehiculos_consultados.map((vehiculo, idx) => (
                            <div 
                              key={idx} 
                              className={`p-4 rounded-lg border-2 ${
                                vehiculo.precios_mercado?.exito
                                  ? vehiculo.precios_mercado.analisis_valor?.estado === "DENTRO_DEL_RANGO"
                                    ? "border-green-200 bg-green-50/50 dark:bg-green-900/10 dark:border-green-800"
                                    : vehiculo.precios_mercado.analisis_valor?.estado === "POR_DEBAJO"
                                      ? "border-yellow-200 bg-yellow-50/50 dark:bg-yellow-900/10 dark:border-yellow-800"
                                      : "border-orange-200 bg-orange-50/50 dark:bg-orange-900/10 dark:border-orange-800"
                                  : "border-slate-200 bg-slate-50/50 dark:bg-slate-800 dark:border-slate-700"
                              }`}
                            >
                              {/* Encabezado del vehículo */}
                              <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-300 font-bold text-sm">
                                    {vehiculo.vehiculo_numero}
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-slate-900 dark:text-white">
                                      {vehiculo.marca} {vehiculo.modelo} {vehiculo.año}
                                    </h4>
                                    <p className="text-xs text-slate-500">
                                      {vehiculo.submodelo && <span className="mr-2">{vehiculo.submodelo}</span>}
                                      {vehiculo.placa && <span className="mr-2">Placa: {vehiculo.placa}</span>}
                                      {vehiculo.color && <span>Color: {vehiculo.color}</span>}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-slate-500">Valor Asegurado</p>
                                  <p className="font-bold text-lg text-slate-900 dark:text-white">
                                    {vehiculo.moneda_valor_asegurado === 'USD' ? 'US$' : 'RD$'} {vehiculo.valor_asegurado?.toLocaleString('es-DO')}
                                  </p>
                                </div>
                              </div>

                              {/* Precios de mercado del vehículo */}
                              {vehiculo.precios_mercado?.exito ? (
                                <div className="space-y-4">
                                  {/* Grid de precios USD */}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="bg-white dark:bg-slate-700/50 p-3 rounded-lg border border-slate-200 dark:border-slate-600">
                                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Encontrados</p>
                                      <p className="text-xl font-bold text-slate-900 dark:text-white">
                                        {vehiculo.precios_mercado.vehiculos_encontrados}
                                      </p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/30 p-3 rounded-lg">
                                      <p className="text-xs text-green-600 dark:text-green-400 mb-1">Mínimo (US$)</p>
                                      <p className="text-lg font-bold text-green-700 dark:text-green-300">
                                        ${vehiculo.precios_mercado.precio_minimo_usd?.toLocaleString('es-DO')}
                                      </p>
                                    </div>
                                    <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">
                                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Promedio (US$)</p>
                                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                                        ${vehiculo.precios_mercado.precio_promedio_usd?.toLocaleString('es-DO')}
                                      </p>
                                    </div>
                                    <div className="bg-purple-50 dark:bg-purple-900/30 p-3 rounded-lg">
                                      <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">Máximo (US$)</p>
                                      <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                                        ${vehiculo.precios_mercado.precio_maximo_usd?.toLocaleString('es-DO')}
                                      </p>
                                    </div>
                                  </div>

                                  {/* Grid de precios RD$ */}
                                  {vehiculo.precios_mercado.precio_minimo_dop && (
                                    <div className="grid grid-cols-3 gap-3">
                                      <div className="bg-green-100 dark:bg-green-900/40 p-3 rounded-lg border border-green-200 dark:border-green-800">
                                        <p className="text-xs text-green-600 dark:text-green-400 mb-1">Mínimo (RD$)</p>
                                        <p className="text-lg font-bold text-green-800 dark:text-green-200">
                                          RD$ {vehiculo.precios_mercado.precio_minimo_dop?.toLocaleString('es-DO')}
                                        </p>
                                      </div>
                                      <div className="bg-blue-100 dark:bg-blue-900/40 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Promedio (RD$)</p>
                                        <p className="text-lg font-bold text-blue-800 dark:text-blue-200">
                                          RD$ {vehiculo.precios_mercado.precio_promedio_dop?.toLocaleString('es-DO')}
                                        </p>
                                      </div>
                                      <div className="bg-purple-100 dark:bg-purple-900/40 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
                                        <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">Máximo (RD$)</p>
                                        <p className="text-lg font-bold text-purple-800 dark:text-purple-200">
                                          RD$ {vehiculo.precios_mercado.precio_maximo_dop?.toLocaleString('es-DO')}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Análisis del valor */}
                                  {vehiculo.precios_mercado.analisis_valor && (
                                    <div className={`p-3 rounded-lg border-l-4 ${
                                      vehiculo.precios_mercado.analisis_valor.estado === "DENTRO_DEL_RANGO"
                                        ? "bg-green-50 dark:bg-green-900/20 border-green-500"
                                        : vehiculo.precios_mercado.analisis_valor.estado === "POR_DEBAJO"
                                          ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500"
                                          : "bg-orange-50 dark:bg-orange-900/20 border-orange-500"
                                    }`}>
                                      <div className="flex items-start gap-2">
                                        {vehiculo.precios_mercado.analisis_valor.estado === "DENTRO_DEL_RANGO" ? (
                                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                        ) : vehiculo.precios_mercado.analisis_valor.estado === "POR_DEBAJO" ? (
                                          <TrendingDown className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                                        ) : (
                                          <TrendingUp className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                        )}
                                        <div>
                                          <p className={`font-semibold text-sm ${
                                            vehiculo.precios_mercado.analisis_valor.estado === "DENTRO_DEL_RANGO"
                                              ? "text-green-700 dark:text-green-300"
                                              : vehiculo.precios_mercado.analisis_valor.estado === "POR_DEBAJO"
                                                ? "text-yellow-700 dark:text-yellow-300"
                                                : "text-orange-700 dark:text-orange-300"
                                          }`}>
                                            {vehiculo.precios_mercado.analisis_valor.estado === "DENTRO_DEL_RANGO"
                                              ? "✓ Valor Adecuado"
                                              : vehiculo.precios_mercado.analisis_valor.estado === "POR_DEBAJO"
                                                ? "⚠ Por Debajo del Mercado"
                                                : "⚡ Por Encima del Mercado"
                                            }
                                          </p>
                                          <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                                            {vehiculo.precios_mercado.analisis_valor.recomendacion}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                                  <AlertCircle className="w-4 h-4 text-slate-400" />
                                  <p className="text-sm text-slate-600 dark:text-slate-300">
                                    {vehiculo.precios_mercado?.mensaje || "No se encontraron precios para este vehículo"}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : comparison.precios_mercado.exito ? (
                        /* Formato legacy para un solo vehículo (compatibilidad hacia atrás) */
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg">
                              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Vehículos Encontrados</p>
                              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {comparison.precios_mercado.vehiculos_encontrados}
                              </p>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
                              <p className="text-xs text-green-600 dark:text-green-400 mb-1">Precio Mínimo</p>
                              <p className="text-xl font-bold text-green-700 dark:text-green-300">
                                US$ {comparison.precios_mercado.precio_minimo?.toLocaleString('es-DO')}
                              </p>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                              <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Precio Promedio</p>
                              <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                                US$ {comparison.precios_mercado.precio_promedio?.toLocaleString('es-DO')}
                              </p>
                            </div>
                            <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg">
                              <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">Precio Máximo</p>
                              <p className="text-xl font-bold text-purple-700 dark:text-purple-300">
                                US$ {comparison.precios_mercado.precio_maximo?.toLocaleString('es-DO')}
                              </p>
                            </div>
                          </div>
                          {comparison.precios_mercado.analisis_valor && (
                            <div className={`p-4 rounded-lg border-l-4 ${
                              comparison.precios_mercado.analisis_valor.estado === "DENTRO_DEL_RANGO"
                                ? "bg-green-50 dark:bg-green-900/20 border-green-500"
                                : comparison.precios_mercado.analisis_valor.estado === "POR_DEBAJO"
                                  ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500"
                                  : "bg-orange-50 dark:bg-orange-900/20 border-orange-500"
                            }`}>
                              <p className="text-sm text-slate-600 dark:text-slate-300">
                                {comparison.precios_mercado.analisis_valor.recomendacion}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                          <AlertCircle className="w-5 h-5 text-slate-400" />
                          <p className="text-slate-600 dark:text-slate-300">
                            {comparison.precios_mercado.mensaje || "No se pudieron obtener precios de mercado"}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Resumen Ejecutivo */}
                {comparison.summary && (
                  <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
                        Resumen Ejecutivo
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                        {comparison.summary}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Informe Ejecutivo Tab */}
            <TabsContent value="ejecutivo" data-testid="tab-content-ejecutivo">
              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                <CardHeader className="border-b border-slate-100 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-lg font-semibold text-blue-900 dark:text-blue-300 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Informe Ejecutivo
                      </CardTitle>
                      <CardDescription className="dark:text-slate-400">
                        Resumen ejecutivo generado con IA de los hallazgos principales
                      </CardDescription>
                    </div>
                    {comparison.informe_ejecutivo && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => generateInformeEjecutivo(true)}
                        disabled={generatingInforme}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20"
                        data-testid="regenerate-informe-btn"
                      >
                        {generatingInforme ? (
                          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Regenerando...</>
                        ) : (
                          <><RotateCw className="w-4 h-4 mr-1" /> Regenerar</>
                        )}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {comparison.informe_ejecutivo ? (
                    <InformeEjecutivoUI informe={comparison.informe_ejecutivo} />
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500 mb-4">El informe ejecutivo se genera automaticamente con IA al descargar el PDF</p>
                      <Button
                        onClick={generateInformeEjecutivo}
                        disabled={generatingInforme}
                        data-testid="generate-informe-btn"
                      >
                        {generatingInforme ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando con IA...</>
                        ) : (
                          <><FileText className="w-4 h-4 mr-2" /> Generar Informe Ejecutivo</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Datos Generales Tab */}
            <TabsContent value="datos">
              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                <CardHeader className="border-b border-slate-100 dark:border-slate-700">
                  <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
                    Datos Generales de las Pólizas
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                        <TableHead className="font-semibold">ÍTEM</TableHead>
                        <TableHead className="font-semibold">PÓLIZA ACTUAL</TableHead>
                        <TableHead className="font-semibold">RENOVACIÓN</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparison.datos_generales_polizas?.length >= 2 ? (
                        <>
                          <TableRow>
                            <TableCell className="font-medium">Tipo de Póliza</TableCell>
                            <TableCell>{comparison.datos_generales_polizas[0]?.tipo_poliza || "N/A"}</TableCell>
                            <TableCell>{comparison.datos_generales_polizas[1]?.tipo_poliza || "N/A"}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Producto / Plan</TableCell>
                            <TableCell>{comparison.datos_generales_polizas[0]?.producto_plan || "N/A"}</TableCell>
                            <TableCell>{comparison.datos_generales_polizas[1]?.producto_plan || "N/A"}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Aseguradora</TableCell>
                            <TableCell>{comparison.datos_generales_polizas[0]?.aseguradora || "N/A"}</TableCell>
                            <TableCell>{comparison.datos_generales_polizas[1]?.aseguradora || "N/A"}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Período de Vigencia</TableCell>
                            <TableCell>{`${comparison.datos_generales_polizas[0]?.vigencia_inicio || "N/A"} - ${comparison.datos_generales_polizas[0]?.vigencia_fin || "N/A"}`}</TableCell>
                            <TableCell>{`${comparison.datos_generales_polizas[1]?.vigencia_inicio || "N/A"} - ${comparison.datos_generales_polizas[1]?.vigencia_fin || "N/A"}`}</TableCell>
                          </TableRow>
                        </>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-500">
                            No hay datos generales disponibles
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tabla Comparativa Tab */}
            <TabsContent value="comparativa">
              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                <CardHeader className="border-b border-slate-100 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20">
                  <CardTitle className="text-lg font-semibold text-blue-900 dark:text-blue-300 flex items-center gap-2">
                    <Scale className="w-5 h-5" />
                    Tabla Comparativa Detallada
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400">
                    Comparación ítem por ítem de ambas pólizas
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {comparison.tabla_comparativa?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-slate-600 text-white">
                            <th className="border border-slate-300 dark:border-slate-600 px-4 py-3 text-left font-semibold w-[20%]">
                              ÍTEM EVALUADO
                            </th>
                            <th className="border border-slate-300 dark:border-slate-600 px-4 py-3 text-left font-semibold w-[27%] bg-blue-600">
                              PÓLIZA ACTUAL
                            </th>
                            <th className="border border-slate-300 dark:border-slate-600 px-4 py-3 text-left font-semibold w-[27%] bg-green-600">
                              RENOVACIÓN
                            </th>
                            <th className="border border-slate-300 dark:border-slate-600 px-4 py-3 text-left font-semibold w-[26%] bg-amber-600">
                              OBSERVACIONES / IMPACTO
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filterTablaComparativa(comparison.tabla_comparativa).map((row, idx) => (
                            <tr 
                              key={idx} 
                              className={`${idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/50'} hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors`}
                            >
                              <td className="border border-slate-200 dark:border-slate-600 px-4 py-3 font-semibold text-slate-900 dark:text-white align-top">
                                {row.item}
                              </td>
                              <td className="border border-slate-200 dark:border-slate-600 px-4 py-3 text-slate-700 dark:text-slate-300 text-sm align-top bg-blue-50/30 dark:bg-blue-900/10">
                                <div className="whitespace-pre-wrap">{isMeaningfulValue(row.poliza_a) ? row.poliza_a : "—"}</div>
                              </td>
                              <td className="border border-slate-200 dark:border-slate-600 px-4 py-3 text-slate-700 dark:text-slate-300 text-sm align-top bg-green-50/30 dark:bg-green-900/10">
                                <div className="whitespace-pre-wrap">{isMeaningfulValue(row.poliza_b) ? row.poliza_b : "—"}</div>
                              </td>
                              <td className="border border-slate-200 dark:border-slate-600 px-4 py-3 text-sm align-top bg-amber-50/30 dark:bg-amber-900/10">
                                {row.observacion ? (
                                  <div className="text-amber-800 dark:text-amber-300 italic whitespace-pre-wrap">
                                    {row.observacion}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {/* Coberturas integradas en la tabla comparativa */}
                          {comparison.detalle_coberturas?.length > 0 && (
                            <>
                              <tr>
                                <td colSpan={4} className="bg-[#0C314C] text-white text-center font-bold py-2 text-sm border border-slate-300 dark:border-slate-600" data-testid="coberturas-separator">
                                  COBERTURAS COMPARADAS
                                </td>
                              </tr>
                              {comparison.detalle_coberturas.map((cob, idx) => {
                                const polAText = [
                                  isMeaningfulValue(cob.poliza_a_limite) ? `Límite: ${cob.poliza_a_limite}` : null,
                                  isMeaningfulValue(cob.poliza_a_deducible) ? `Deducible: ${cob.poliza_a_deducible}` : null,
                                  isMeaningfulValue(cob.poliza_a_prima) ? `Prima: ${cob.poliza_a_prima}` : null,
                                ].filter(Boolean).join("\n") || "N/A";
                                const polBText = [
                                  cob.poliza_b_limite ? `Límite: ${cob.poliza_b_limite}` : null,
                                  isMeaningfulValue(cob.poliza_b_deducible) ? `Deducible: ${cob.poliza_b_deducible}` : null,
                                  isMeaningfulValue(cob.poliza_b_prima) ? `Prima: ${cob.poliza_b_prima}` : null,
                                ].filter(Boolean).join("\n") || "N/A";
                                let obs = compareCoverageObservation(cob);
                                return (
                                  <tr 
                                    key={`cob-${idx}`} 
                                    className={`${idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/50'} hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors`}
                                    data-testid={`cobertura-row-${idx}`}
                                  >
                                    <td className="border border-slate-200 dark:border-slate-600 px-4 py-3 font-semibold text-indigo-900 dark:text-indigo-300 align-top">
                                      {cob.cobertura}
                                    </td>
                                    <td className="border border-slate-200 dark:border-slate-600 px-4 py-3 text-slate-700 dark:text-slate-300 text-sm align-top bg-blue-50/30 dark:bg-blue-900/10">
                                      <div className="whitespace-pre-wrap">{polAText}</div>
                                    </td>
                                    <td className="border border-slate-200 dark:border-slate-600 px-4 py-3 text-slate-700 dark:text-slate-300 text-sm align-top bg-green-50/30 dark:bg-green-900/10">
                                      <div className="whitespace-pre-wrap">{polBText}</div>
                                    </td>
                                    <td className={`border border-slate-200 dark:border-slate-600 px-4 py-3 text-sm align-top ${
                                      obs === "No existen diferencias en la comparación"
                                        ? "bg-emerald-50/40 dark:bg-emerald-900/10"
                                        : "bg-amber-50/30 dark:bg-amber-900/10"
                                    }`}>
                                      <div className={
                                        obs === "No existen diferencias en la comparación"
                                          ? "text-emerald-700 dark:text-emerald-400 font-medium"
                                          : "text-amber-800 dark:text-amber-300 italic"
                                      }>{obs}</div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-slate-500 dark:text-slate-400">No hay datos comparativos disponibles</p>
                    </div>
                  )}

                  {/* Análisis de Valor de Mercado dentro del tab comparativa */}
                  {comparison.precios_mercado && (
                    <div className="p-6 border-t border-slate-200 dark:border-slate-700" data-testid="valor-mercado-comparativa">
                      <h3 className="text-lg font-bold text-purple-800 dark:text-purple-300 flex items-center gap-2 mb-4">
                        <TrendingUp className="w-5 h-5" />
                        Análisis de Valor de Mercado
                      </h3>
                      {comparison.precios_mercado.tasa_cambio && (
                        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">
                            Tasa de cambio: <span className="font-bold">1 US$ = RD$ {comparison.precios_mercado.tasa_cambio}</span>
                          </span>
                          <span className="text-xs text-slate-400">({comparison.precios_mercado.fuente_tasa_cambio || "BCRD"})</span>
                        </div>
                      )}
                      {comparison.precios_mercado.vehiculos_consultados?.length > 0 ? (
                        <div className="space-y-4">
                          {comparison.precios_mercado.vehiculos_consultados.map((vehiculo, vidx) => (
                            <div key={vidx} className={`rounded-lg border p-4 ${
                              vehiculo.precios_mercado?.exito
                                ? vehiculo.precios_mercado.analisis_valor?.estado === "DENTRO_DEL_RANGO"
                                  ? "border-green-200 bg-green-50/50 dark:bg-green-900/10"
                                  : vehiculo.precios_mercado.analisis_valor?.estado === "POR_DEBAJO"
                                    ? "border-yellow-200 bg-yellow-50/50 dark:bg-yellow-900/10"
                                    : "border-orange-200 bg-orange-50/50 dark:bg-orange-900/10"
                                : "border-slate-200 bg-slate-50 dark:bg-slate-800"
                            }`}>
                              <p className="font-semibold text-sm text-slate-800 dark:text-slate-200 mb-2">
                                {vehiculo.marca} {vehiculo.modelo} {vehiculo.año} {vehiculo.placa && `- Placa: ${vehiculo.placa}`}
                                {vehiculo.precios_mercado?.exito && (
                                  <span className="ml-2 text-xs text-slate-500">
                                    Encontrados: {vehiculo.precios_mercado.vehiculos_encontrados}
                                  </span>
                                )}
                                {vehiculo.valor_asegurado && (
                                  <span className="ml-2 text-xs font-bold text-blue-600">
                                    Valor Asegurado: {vehiculo.moneda_valor_asegurado || 'RD$'} {vehiculo.valor_asegurado?.toLocaleString('es-DO')}
                                  </span>
                                )}
                              </p>
                              {vehiculo.precios_mercado?.exito ? (
                                <>
                                  <div className="grid grid-cols-3 gap-3 mb-3">
                                    <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                      <p className="text-[10px] text-blue-600 uppercase">Mínimo (US$)</p>
                                      <p className="font-bold text-blue-700 dark:text-blue-300">${vehiculo.precios_mercado.precio_minimo_usd?.toLocaleString('es-DO')}</p>
                                    </div>
                                    <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                                      <p className="text-[10px] text-green-600 uppercase">Promedio (US$)</p>
                                      <p className="font-bold text-green-700 dark:text-green-300">${vehiculo.precios_mercado.precio_promedio_usd?.toLocaleString('es-DO')}</p>
                                    </div>
                                    <div className="text-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                                      <p className="text-[10px] text-purple-600 uppercase">Máximo (US$)</p>
                                      <p className="font-bold text-purple-700 dark:text-purple-300">${vehiculo.precios_mercado.precio_maximo_usd?.toLocaleString('es-DO')}</p>
                                    </div>
                                  </div>
                                  {vehiculo.precios_mercado.precio_minimo_dop && (
                                    <div className="grid grid-cols-3 gap-3 mb-3">
                                      <div className="text-center p-2 bg-green-100 dark:bg-green-900/30 rounded border border-green-200 dark:border-green-800">
                                        <p className="text-[10px] text-green-600 uppercase">Mínimo (RD$)</p>
                                        <p className="font-bold text-green-800 dark:text-green-300">RD$ {vehiculo.precios_mercado.precio_minimo_dop?.toLocaleString('es-DO')}</p>
                                      </div>
                                      <div className="text-center p-2 bg-blue-100 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-800">
                                        <p className="text-[10px] text-blue-600 uppercase">Promedio (RD$)</p>
                                        <p className="font-bold text-blue-800 dark:text-blue-300">RD$ {vehiculo.precios_mercado.precio_promedio_dop?.toLocaleString('es-DO')}</p>
                                      </div>
                                      <div className="text-center p-2 bg-purple-100 dark:bg-purple-900/30 rounded border border-purple-200 dark:border-purple-800">
                                        <p className="text-[10px] text-purple-600 uppercase">Máximo (RD$)</p>
                                        <p className="font-bold text-purple-800 dark:text-purple-300">RD$ {vehiculo.precios_mercado.precio_maximo_dop?.toLocaleString('es-DO')}</p>
                                      </div>
                                    </div>
                                  )}
                                  {vehiculo.precios_mercado.analisis_valor && (
                                    <div className={`p-3 rounded-lg border-l-4 text-sm ${
                                      vehiculo.precios_mercado.analisis_valor.estado === "DENTRO_DEL_RANGO"
                                        ? "bg-green-50 border-green-500 dark:bg-green-900/20"
                                        : vehiculo.precios_mercado.analisis_valor.estado === "POR_DEBAJO"
                                          ? "bg-yellow-50 border-yellow-500 dark:bg-yellow-900/20"
                                          : "bg-orange-50 border-orange-500 dark:bg-orange-900/20"
                                    }`}>
                                      <p className="font-semibold">{vehiculo.precios_mercado.analisis_valor.recomendacion}</p>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-slate-500 italic">{vehiculo.precios_mercado?.mensaje || "No se encontraron precios"}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : comparison.precios_mercado.exito ? (
                        <>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                            Búsqueda: {comparison.precios_mercado.marca} {comparison.precios_mercado.modelo} {comparison.precios_mercado.año}
                            {" | "}Encontrados: {comparison.precios_mercado.vehiculos_encontrados}
                          </p>
                          <div className="grid grid-cols-3 gap-3 mb-3">
                            <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                              <p className="text-[10px] text-blue-600 uppercase">Mínimo</p>
                              <p className="font-bold text-blue-700">US$ {comparison.precios_mercado.precio_minimo?.toLocaleString('es-DO')}</p>
                            </div>
                            <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                              <p className="text-[10px] text-green-600 uppercase">Promedio</p>
                              <p className="font-bold text-green-700">US$ {comparison.precios_mercado.precio_promedio?.toLocaleString('es-DO')}</p>
                            </div>
                            <div className="text-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                              <p className="text-[10px] text-purple-600 uppercase">Máximo</p>
                              <p className="font-bold text-purple-700">US$ {comparison.precios_mercado.precio_maximo?.toLocaleString('es-DO')}</p>
                            </div>
                          </div>
                          {comparison.precios_mercado.analisis_valor && (
                            <div className={`p-3 rounded-lg border-l-4 text-sm ${
                              comparison.precios_mercado.analisis_valor.estado === "DENTRO_DEL_RANGO"
                                ? "bg-green-50 border-green-500"
                                : comparison.precios_mercado.analisis_valor.estado === "POR_DEBAJO"
                                  ? "bg-yellow-50 border-yellow-500"
                                  : "bg-orange-50 border-orange-500"
                            }`}>
                              <p className="font-semibold">{comparison.precios_mercado.analisis_valor.recomendacion}</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500 italic">
                          {comparison.precios_mercado.mensaje || "No se pudieron obtener precios de mercado"}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Coberturas Tab: eliminada por requerimiento del cliente */}
            {false && (
              <TabsContent value="coberturas">
                <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="detalle-coberturas-card">
                  <CardHeader className="border-b border-slate-100 dark:border-slate-700 bg-indigo-50 dark:bg-indigo-900/20">
                    <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-indigo-500" />
                      Detalle de Coberturas - Límites, Deducibles y Primas
                    </CardTitle>
                    <CardDescription className="dark:text-slate-400">
                      Comparación detallada de cada cobertura entre ambas pólizas
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr>
                            <th rowSpan={2} className="border-b border-r p-2 text-left bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-semibold">Cobertura</th>
                            <th colSpan={3} className="border-b border-r p-2 text-center bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold">Póliza Actual</th>
                            <th colSpan={3} className="border-b p-2 text-center bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-semibold">Renovación</th>
                          </tr>
                          <tr className="text-xs">
                            <th className="border-b border-r p-1.5 text-center bg-blue-50/50 dark:bg-blue-900/20 text-slate-600 dark:text-slate-400">Límite</th>
                            <th className="border-b border-r p-1.5 text-center bg-blue-50/50 dark:bg-blue-900/20 text-slate-600 dark:text-slate-400">Deducible</th>
                            <th className="border-b border-r p-1.5 text-center bg-blue-50/50 dark:bg-blue-900/20 text-slate-600 dark:text-slate-400">Prima</th>
                            <th className="border-b border-r p-1.5 text-center bg-green-50/50 dark:bg-green-900/20 text-slate-600 dark:text-slate-400">Límite</th>
                            <th className="border-b border-r p-1.5 text-center bg-green-50/50 dark:bg-green-900/20 text-slate-600 dark:text-slate-400">Deducible</th>
                            <th className="border-b p-1.5 text-center bg-green-50/50 dark:bg-green-900/20 text-slate-600 dark:text-slate-400">Prima</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparison.detalle_coberturas.map((cob, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? "bg-white dark:bg-slate-800" : "bg-slate-50 dark:bg-slate-800/50"}>
                              <td className="border-r p-2 font-medium text-slate-900 dark:text-white text-sm">{cob.cobertura}</td>
                              <td className="border-r p-1.5 text-center text-xs text-slate-700 dark:text-slate-300">{cob.poliza_a_limite || "-"}</td>
                              <td className="border-r p-1.5 text-center text-xs text-slate-700 dark:text-slate-300">{cob.poliza_a_deducible || "-"}</td>
                              <td className="border-r p-1.5 text-center text-xs text-slate-700 dark:text-slate-300">{cob.poliza_a_prima || "-"}</td>
                              <td className="border-r p-1.5 text-center text-xs text-slate-700 dark:text-slate-300">{cob.poliza_b_limite || "-"}</td>
                              <td className="border-r p-1.5 text-center text-xs text-slate-700 dark:text-slate-300">{cob.poliza_b_deducible || "-"}</td>
                              <td className="p-1.5 text-center text-xs text-slate-700 dark:text-slate-300">{cob.poliza_b_prima || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Escenarios Tab */}
            <TabsContent value="escenarios">
              <div className="space-y-6">
                <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      Análisis de Escenarios Críticos
                    </CardTitle>
                    <CardDescription className="dark:text-slate-400">
                      Simulación de siniestros para evaluar la respuesta de cada póliza
                    </CardDescription>
                  </CardHeader>
                </Card>

                {comparison.escenarios_criticos?.map((escenario, idx) => (
                  <Card key={idx} className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                    <CardHeader className="border-b border-slate-100 dark:border-slate-700 bg-orange-50 dark:bg-orange-900/20">
                      <CardTitle className="text-base font-semibold text-slate-900 dark:text-white">
                        Escenario {idx + 1}: {escenario.nombre}
                      </CardTitle>
                      {escenario.descripcion && (
                        <CardDescription className="dark:text-slate-400">{escenario.descripcion}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="p-0">
                      {escenario.flujo_reclamacion?.length > 0 && (
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                              <TableHead className="font-semibold">Flujo de la Reclamación</TableHead>
                              <TableHead className="font-semibold">Póliza Actual</TableHead>
                              <TableHead className="font-semibold">Renovación</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {escenario.flujo_reclamacion.map((flujo, fIdx) => (
                              <TableRow key={fIdx}>
                                <TableCell className="font-medium">{flujo.concepto}</TableCell>
                                <TableCell className={flujo.concepto.toLowerCase().includes("déficit") ? "text-red-600 font-semibold" : ""}>
                                  {flujo.poliza_a || "N/A"}
                                </TableCell>
                                <TableCell className={flujo.concepto.toLowerCase().includes("déficit") ? "text-red-600 font-semibold" : ""}>
                                  {flujo.poliza_b || "N/A"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                      {escenario.analisis_impacto && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-t border-slate-100 dark:border-slate-700">
                          <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">Análisis de Impacto:</p>
                          <p className="text-sm text-blue-800 dark:text-blue-200">{escenario.analisis_impacto}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Dictamen Tab */}
            <TabsContent value="dictamen">
              <div className="space-y-6">
                {/* Historial: recomendaciones de comparaciones anteriores del mismo cliente/póliza */}
                {comparison.historial_recomendaciones && (
                  <Card className="border-amber-200 dark:border-amber-800 shadow-sm bg-amber-50 dark:bg-amber-900/20" data-testid="historial-recomendaciones-card">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-amber-900 dark:text-amber-300 flex items-center gap-2">
                        <RotateCw className="w-5 h-5" />
                        Historial: Comparación Anterior de este Cliente/Póliza
                        {(comparison.historial_recomendaciones.fecha_anterior || comparison.historial_match?.fecha) && (
                          <Badge variant="outline" className="ml-2 text-xs font-normal">
                            {comparison.historial_recomendaciones.fecha_anterior || String(comparison.historial_match?.fecha || "").slice(0, 10)}
                          </Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {comparison.historial_recomendaciones.recomendaciones_vigentes?.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Recomendaciones anteriores que SIGUEN vigentes:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            {comparison.historial_recomendaciones.recomendaciones_vigentes.map((r, i) => (
                              <li key={i} className="text-sm text-slate-700 dark:text-slate-300">{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {comparison.historial_recomendaciones.cambios_desde_ultima?.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Cambios desde la última comparación:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            {comparison.historial_recomendaciones.cambios_desde_ultima.map((c, i) => (
                              <li key={i} className="text-sm text-slate-700 dark:text-slate-300">{c}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {comparison.historial_recomendaciones.nota && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 italic border-t border-amber-200 dark:border-amber-800 pt-2">
                          {comparison.historial_recomendaciones.nota}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {comparison.dictamen_pericial && (
                  <>
                    {/* Alerta Fundamental */}
                    {comparison.dictamen_pericial.alerta_fundamental && (
                      <Card className="border-red-200 dark:border-red-800 shadow-sm bg-red-50 dark:bg-red-900/20">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-bold text-red-800 dark:text-red-300 mb-1">ALERTA ROJA FUNDAMENTAL</p>
                              <p className="text-red-700 dark:text-red-200">{comparison.dictamen_pericial.alerta_fundamental}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Hipótesis */}
                    {comparison.dictamen_pericial.hipotesis_estrategica && (
                      <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                        <CardHeader>
                          <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-blue-600" />
                            Hipótesis Estratégica
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-slate-700 dark:text-slate-300">{comparison.dictamen_pericial.hipotesis_estrategica}</p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Calificaciones */}
                    {comparison.dictamen_pericial.calificaciones?.length > 0 && (
                      <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                        <CardHeader>
                          <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                            <Scale className="w-5 h-5 text-purple-600" />
                            Evaluación Pericial
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                                <TableHead>Criterio</TableHead>
                                <TableHead>Póliza Actual</TableHead>
                                <TableHead>Renovación</TableHead>
                                <TableHead>Justificación</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {comparison.dictamen_pericial.calificaciones.map((cal, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="font-medium">{cal.criterio}</TableCell>
                                  <TableCell>{getCalificacionBadge(cal.poliza_a)}</TableCell>
                                  <TableCell>{getCalificacionBadge(cal.poliza_b)}</TableCell>
                                  <TableCell className="text-sm text-slate-600 dark:text-slate-400">{cal.justificacion}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}

                    {/* Análisis y Recomendación */}
                    <Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-900/20">
                      <CardHeader>
                        <CardTitle className="text-lg font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5" />
                          Dictamen y Recomendación Final
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {comparison.dictamen_pericial.recomendacion_final && (
                          <div className="pt-4 border-t border-green-200 dark:border-green-700">
                            <p className="font-semibold text-green-800 dark:text-green-300 mb-2">Recomendación Final:</p>
                            <p className="text-green-700 dark:text-green-200 font-medium">{comparison.dictamen_pericial.recomendacion_final}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Nota del Asesor */}
                    {comparison.nota_asesor && (
                      <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                        <CardContent className="p-4">
                          <p className="text-sm italic text-slate-600 dark:text-slate-400">
                            <span className="font-semibold">Nota del Asesor (QAsesores SRL):</span> {comparison.nota_asesor}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </TabsContent>

            {/* Comunicación Tab */}
            <TabsContent value="comunicacion">
              <div className="space-y-6">
                {/* Comunicación con Aseguradora */}
                {comparison.comunicacion_aseguradora?.cuerpo && (
                  <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                    <CardHeader className="border-b border-slate-100 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20">
                      <CardTitle className="text-base font-semibold text-blue-900 dark:text-blue-300 flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Borrador de Correo para la Aseguradora
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {comparison.comunicacion_aseguradora.asunto && (
                        <div className="mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                          <p className="text-sm text-slate-500 dark:text-slate-400">Asunto:</p>
                          <p className="font-medium text-slate-900 dark:text-white">{comparison.comunicacion_aseguradora.asunto}</p>
                        </div>
                      )}
                      <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{comparison.comunicacion_aseguradora.cuerpo}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Comunicación con Cliente */}
                {comparison.comunicacion_cliente?.cuerpo && (
                  <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
                    <CardHeader className="border-b border-slate-100 dark:border-slate-700 bg-green-50 dark:bg-green-900/20">
                      <CardTitle className="text-base font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        Borrador de Carta de Renovación para el Cliente
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {comparison.comunicacion_cliente.saludo && (
                        <p className="font-medium text-slate-900 dark:text-white mb-4">{comparison.comunicacion_cliente.saludo}</p>
                      )}
                      <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap mb-4">{comparison.comunicacion_cliente.cuerpo}</p>
                      
                      {comparison.comunicacion_cliente.datos_propuesta && (
                        <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <p className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            Propuesta Económica:
                          </p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {comparison.comunicacion_cliente.datos_propuesta.monto_asegurado && (
                              <div>
                                <p className="text-slate-500 dark:text-slate-400">Monto Asegurado:</p>
                                <p className="font-semibold text-slate-900 dark:text-white">{comparison.comunicacion_cliente.datos_propuesta.monto_asegurado}</p>
                              </div>
                            )}
                            {comparison.comunicacion_cliente.datos_propuesta.prima_neta && (
                              <div>
                                <p className="text-slate-500 dark:text-slate-400">Prima Neta:</p>
                                <p className="font-semibold text-slate-900 dark:text-white">{comparison.comunicacion_cliente.datos_propuesta.prima_neta}</p>
                              </div>
                            )}
                            {comparison.comunicacion_cliente.datos_propuesta.impuestos && (
                              <div>
                                <p className="text-slate-500 dark:text-slate-400">Impuestos:</p>
                                <p className="font-semibold text-slate-900 dark:text-white">{comparison.comunicacion_cliente.datos_propuesta.impuestos}</p>
                              </div>
                            )}
                            {comparison.comunicacion_cliente.datos_propuesta.prima_total && (
                              <div>
                                <p className="text-slate-500 dark:text-slate-400">Prima Total:</p>
                                <p className="font-bold text-green-600 dark:text-green-400 text-lg">{comparison.comunicacion_cliente.datos_propuesta.prima_total}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

          </Tabs>
        )}

        {/* Legacy Format - For backward compatibility */}
        {comparison.status === "completed" && !isDetailedFormat && (
          <Tabs defaultValue="summary" className="space-y-6">
            <TabsList className="bg-white border border-slate-200 p-1 flex-wrap">
              <TabsTrigger value="summary">Resumen</TabsTrigger>
              <TabsTrigger value="differences">Diferencias</TabsTrigger>
              {comparison.score && <TabsTrigger value="score">Puntaje</TabsTrigger>}
              {comparison.financial_comparison && <TabsTrigger value="financial">Financiero</TabsTrigger>}
              {comparison.risk_assessment && <TabsTrigger value="risks">Riesgos</TabsTrigger>}
            </TabsList>

            <TabsContent value="summary">
              <div className="space-y-6">
                {/* Score cards if available */}
                {comparison.score && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {["document1", "document2"].map((dk, i) => {
                      const s = comparison.score[dk];
                      if (!s) return null;
                      const name = i === 0 ? comparison.document1_name : comparison.document2_name;
                      return (
                        <Card key={dk} className="border-slate-200 shadow-sm" data-testid={`score-card-${dk}`}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold text-slate-700">Póliza {i === 0 ? "A" : "B"}: {name}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-center gap-4 mb-3">
                              <div className="text-4xl font-bold text-blue-700">{s.total || 0}</div>
                              <span className="text-sm text-slate-400">/ 100</span>
                            </div>
                            <div className="space-y-2">
                              {[{label: "Cobertura", key: "cobertura", color: "bg-green-500"},
                                {label: "Precio", key: "precio", color: "bg-blue-500"},
                                {label: "Condiciones", key: "condiciones", color: "bg-purple-500"}].map(item => (
                                <div key={item.key}>
                                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                                    <span>{item.label}</span><span>{s[item.key] || 0}/100</span>
                                  </div>
                                  <div className="w-full bg-slate-100 rounded-full h-2">
                                    <div className={`${item.color} h-2 rounded-full transition-all`} style={{width: `${s[item.key] || 0}%`}} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
                {comparison.score?.explanation && (
                  <Card className="border-slate-200 shadow-sm">
                    <CardContent className="py-4">
                      <p className="text-sm text-slate-600 italic">{comparison.score.explanation}</p>
                    </CardContent>
                  </Card>
                )}

                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900">Resumen Ejecutivo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {comparison.summary || comparison.raw_analysis || "No hay resumen disponible."}
                    </p>
                  </CardContent>
                </Card>

                {comparison.recommendation && (
                  <Card className="border-green-200 bg-green-50 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold text-green-800">Recomendación</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-green-900 leading-relaxed whitespace-pre-wrap">{comparison.recommendation}</p>
                    </CardContent>
                  </Card>
                )}

                {comparison.best_practices && (
                  <Card className="border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold text-slate-900">Mejores Prácticas y Evaluación de Riesgos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{comparison.best_practices}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="differences">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900">Diferencias Identificadas</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {comparison.differences?.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {comparison.differences.map((diff, idx) => (
                        <div key={idx} className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-slate-900">{diff.aspect}</h4>
                            <div className="flex gap-2">
                              {diff.confidence && <Badge variant="outline" className="text-xs">{diff.confidence}</Badge>}
                              {getImportanceBadge(diff.importance)}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-slate-50 p-3 rounded">
                              <p className="text-xs font-medium text-slate-500 mb-1">Póliza Actual</p>
                              <p className="text-sm text-slate-700">{diff.document1 || "N/A"}</p>
                            </div>
                            <div className="bg-slate-50 p-3 rounded">
                              <p className="text-xs font-medium text-slate-500 mb-1">Renovación</p>
                              <p className="text-sm text-slate-700">{diff.document2 || "N/A"}</p>
                            </div>
                          </div>
                          {diff.impact && <p className="text-xs text-slate-500 mt-2 italic">Impacto: {diff.impact}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-slate-600">No se identificaron diferencias significativas</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {comparison.score && (
              <TabsContent value="score">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900">Puntaje Comparativo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {["document1", "document2"].map((dk, i) => {
                        const s = comparison.score[dk];
                        if (!s) return null;
                        const name = i === 0 ? comparison.document1_name : comparison.document2_name;
                        return (
                          <div key={dk} className="p-4 border rounded-lg">
                            <h4 className="font-semibold mb-3">Póliza {i === 0 ? "A" : "B"}: {name}</h4>
                            <div className="text-5xl font-bold text-center my-4 text-blue-700">{s.total || 0}<span className="text-lg text-slate-400">/100</span></div>
                            {[{label: "Cobertura", key: "cobertura"}, {label: "Precio", key: "precio"}, {label: "Condiciones", key: "condiciones"}].map(item => (
                              <div key={item.key} className="flex justify-between py-2 border-t text-sm">
                                <span className="text-slate-600">{item.label}</span>
                                <span className="font-semibold">{s[item.key] || 0}/100</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                    {comparison.score.explanation && <p className="mt-4 text-sm text-slate-600 italic">{comparison.score.explanation}</p>}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {comparison.financial_comparison && (
              <TabsContent value="financial">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900">Comparación Financiera</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-xs text-blue-600 mb-1">Prima Póliza Actual</p>
                        <p className="text-xl font-bold text-blue-800">{comparison.financial_comparison.prima_doc1 || "N/A"}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-xs text-green-600 mb-1">Prima Renovación</p>
                        <p className="text-xl font-bold text-green-800">{comparison.financial_comparison.prima_doc2 || "N/A"}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-xs text-purple-600 mb-1">Diferencia</p>
                        <p className="text-xl font-bold text-purple-800">{comparison.financial_comparison.diferencia_prima || "N/A"}</p>
                      </div>
                    </div>
                    {comparison.financial_comparison.costo_beneficio && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm font-semibold text-slate-700 mb-1">Análisis Costo/Beneficio:</p>
                        <p className="text-sm text-slate-600">{comparison.financial_comparison.costo_beneficio}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {comparison.risk_assessment && (
              <TabsContent value="risks">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900">Evaluación de Riesgos</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {comparison.risk_assessment.covered_risks?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-green-700 mb-2">Riesgos Bien Cubiertos</h4>
                        <ul className="space-y-1">
                          {comparison.risk_assessment.covered_risks.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {comparison.risk_assessment.gaps?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-red-700 mb-2">Gaps de Cobertura</h4>
                        <ul className="space-y-1">
                          {comparison.risk_assessment.gaps.map((g, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />{g}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {comparison.risk_assessment.suggestions?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-blue-700 mb-2">Sugerencias</h4>
                        <ul className="space-y-1">
                          {comparison.risk_assessment.suggestions.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        )}

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-5xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Vista Previa del Análisis Comparativo
              </DialogTitle>
            </DialogHeader>
            
            <ScrollArea className="h-[65vh] w-full">
              <div ref={reportRef} className="bg-white p-8 rounded-lg border border-slate-200 print-content">
                {/* Report Header */}
                <div className="text-center border-b-2 border-blue-600 pb-6 mb-6">
                  <h1 className="text-2xl font-bold text-blue-900">
                    ANÁLISIS COMPARATIVO Y RECOMENDACIÓN DE PÓLIZA
                  </h1>
                  <p className="text-slate-500 mt-2">
                    Fecha: {new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                </div>

                {/* Datos del Asegurado */}
                {comparison?.datos_asegurado && (
                  <div className="mb-6">
                    <h2 className="text-lg font-bold text-blue-800 border-b border-slate-200 pb-2 mb-4">DATOS DEL ASEGURADO</h2>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b">
                          <td className="py-2 font-semibold w-1/4">Nombre / Razón Social</td>
                          <td className="py-2">{comparison.datos_asegurado.nombre_razon_social || "No especificado"}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 font-semibold">Identificación (RNC)</td>
                          <td className="py-2">{comparison.datos_asegurado.identificacion_rnc || "No especificado"}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 font-semibold">Dirección</td>
                          <td className="py-2">{comparison.datos_asegurado.direccion || "No especificado"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Datos del Vehículo (solo si es un vehículo, no flotilla) */}
                {comparison?.datos_vehiculo?.es_poliza_vehiculo && !((comparison?.precios_mercado?.total_vehiculos || 0) > 1 || (comparison?.precios_mercado?.vehiculos_consultados?.length || 0) > 1) && (
                  <div className="mb-6">
                    <h2 className="text-lg font-bold text-blue-800 border-b border-slate-200 pb-2 mb-4">DATOS DEL VEHÍCULO</h2>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Marca:</span>
                        <p className="font-semibold">{comparison.datos_vehiculo.marca || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Modelo:</span>
                        <p className="font-semibold">{comparison.datos_vehiculo.modelo || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Año:</span>
                        <p className="font-semibold">{comparison.datos_vehiculo.año || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Versión/Submodelo:</span>
                        <p className="font-semibold">{comparison.datos_vehiculo.submodelo || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Color:</span>
                        <p className="font-medium">{comparison.datos_vehiculo.color || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Placa:</span>
                        <p className="font-medium">{comparison.datos_vehiculo.placa || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Chasis:</span>
                        <p className="font-medium text-xs">{comparison.datos_vehiculo.chasis || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Valor Asegurado:</span>
                        <p className="font-bold text-blue-600">
                          {comparison.datos_vehiculo.valor_asegurado 
                            ? `${comparison.datos_vehiculo.moneda_valor_asegurado || comparison.datos_vehiculo.moneda_valor_asegurado_normalizada || 'RD$'} ${comparison.datos_vehiculo.valor_asegurado.toLocaleString('es-DO')}`
                            : "N/A"
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Análisis de Valor de Mercado */}
                {comparison?.precios_mercado && (
                  <div className="mb-6">
                    <h2 className="text-lg font-bold text-purple-800 border-b border-slate-200 pb-2 mb-4">
                      ANÁLISIS DE VALOR DE MERCADO
                    </h2>
                    
                    {comparison.precios_mercado.tasa_cambio && (
                      <p className="text-sm font-semibold text-slate-700 mb-3">
                        Tasa de cambio: 1 US$ = RD$ {comparison.precios_mercado.tasa_cambio} ({comparison.precios_mercado.fuente_tasa_cambio || "BCRD"})
                      </p>
                    )}

                    {comparison.precios_mercado.vehiculos_consultados?.length > 0 ? (
                      <div className="space-y-3">
                        {comparison.precios_mercado.vehiculos_consultados.slice(0, 4).map((vehiculo, vIdx) => (
                          <div key={vIdx} className="border border-slate-200 rounded p-3">
                            <p className="font-semibold text-sm text-slate-800 mb-1">
                              {vehiculo.marca} {vehiculo.modelo} {vehiculo.año} {vehiculo.placa && `- ${vehiculo.placa}`}
                              {vehiculo.valor_asegurado && (
                                <span className="ml-2 text-blue-600 text-xs">
                                  V.A.: {vehiculo.moneda_valor_asegurado || 'RD$'} {vehiculo.valor_asegurado?.toLocaleString('es-DO')}
                                </span>
                              )}
                            </p>
                            {vehiculo.precios_mercado?.exito ? (
                              <>
                                <div className="grid grid-cols-3 gap-2 text-xs mb-1">
                                  <div className="text-center p-1 bg-blue-50 rounded">
                                    <span className="text-blue-600">Mín: </span>
                                    <span className="font-bold">${vehiculo.precios_mercado.precio_minimo_usd?.toLocaleString('es-DO')}</span>
                                  </div>
                                  <div className="text-center p-1 bg-green-50 rounded">
                                    <span className="text-green-600">Prom: </span>
                                    <span className="font-bold">${vehiculo.precios_mercado.precio_promedio_usd?.toLocaleString('es-DO')}</span>
                                  </div>
                                  <div className="text-center p-1 bg-purple-50 rounded">
                                    <span className="text-purple-600">Máx: </span>
                                    <span className="font-bold">${vehiculo.precios_mercado.precio_maximo_usd?.toLocaleString('es-DO')}</span>
                                  </div>
                                </div>
                                {vehiculo.precios_mercado.analisis_valor && (
                                  <p className="text-xs italic text-slate-600">{vehiculo.precios_mercado.analisis_valor.recomendacion}</p>
                                )}
                              </>
                            ) : (
                              <p className="text-xs italic text-slate-400">{vehiculo.precios_mercado?.mensaje || "Datos insuficientes"}</p>
                            )}
                          </div>
                        ))}
                        {comparison.precios_mercado.vehiculos_consultados.length > 4 && (
                          <p className="text-xs text-slate-500 italic">
                            ... y {comparison.precios_mercado.vehiculos_consultados.length - 4} vehículos más en el PDF completo
                          </p>
                        )}
                      </div>
                    ) : comparison.precios_mercado.exito ? (
                      <>
                        <p className="text-sm text-slate-600 mb-3">
                          Búsqueda: {comparison.precios_mercado.marca} {comparison.precios_mercado.modelo} {comparison.precios_mercado.año}
                          {" | "}Encontrados: {comparison.precios_mercado.vehiculos_encontrados}
                        </p>
                        <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                          <div className="text-center p-2 bg-green-50 rounded">
                            <p className="text-xs text-green-600">Mínimo</p>
                            <p className="font-bold text-green-700">US$ {comparison.precios_mercado.precio_minimo?.toLocaleString('es-DO')}</p>
                          </div>
                          <div className="text-center p-2 bg-blue-50 rounded">
                            <p className="text-xs text-blue-600">Promedio</p>
                            <p className="font-bold text-blue-700">US$ {comparison.precios_mercado.precio_promedio?.toLocaleString('es-DO')}</p>
                          </div>
                          <div className="text-center p-2 bg-purple-50 rounded">
                            <p className="text-xs text-purple-600">Máximo</p>
                            <p className="font-bold text-purple-700">US$ {comparison.precios_mercado.precio_maximo?.toLocaleString('es-DO')}</p>
                          </div>
                        </div>
                        {comparison.precios_mercado.analisis_valor && (
                          <p className="text-sm italic text-slate-600">{comparison.precios_mercado.analisis_valor.recomendacion}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-500 italic text-sm">
                        {comparison.precios_mercado.mensaje || "No se pudieron obtener precios de mercado."}
                      </p>
                    )}
                  </div>
                )}

                {/* Datos Generales */}
                {comparison?.datos_generales_polizas?.length >= 2 && (
                  <div className="mb-6">
                    <h2 className="text-lg font-bold text-blue-800 border-b border-slate-200 pb-2 mb-4">DATOS GENERALES DE LAS PÓLIZAS</h2>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="border p-2 text-left">ÍTEM</th>
                          <th className="border p-2 text-center">PÓLIZA ACTUAL</th>
                          <th className="border p-2 text-center">RENOVACIÓN</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border p-2">Tipo de Póliza</td>
                          <td className="border p-2 text-center">{comparison.datos_generales_polizas[0]?.tipo_poliza}</td>
                          <td className="border p-2 text-center">{comparison.datos_generales_polizas[1]?.tipo_poliza}</td>
                        </tr>
                        <tr>
                          <td className="border p-2">Aseguradora</td>
                          <td className="border p-2 text-center">{comparison.datos_generales_polizas[0]?.aseguradora}</td>
                          <td className="border p-2 text-center">{comparison.datos_generales_polizas[1]?.aseguradora}</td>
                        </tr>
                        <tr>
                          <td className="border p-2">Vigencia</td>
                          <td className="border p-2 text-center">{comparison.datos_generales_polizas[0]?.vigencia_inicio} - {comparison.datos_generales_polizas[0]?.vigencia_fin}</td>
                          <td className="border p-2 text-center">{comparison.datos_generales_polizas[1]?.vigencia_inicio} - {comparison.datos_generales_polizas[1]?.vigencia_fin}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Tabla Comparativa Detallada en Vista Previa */}
                {comparison?.tabla_comparativa?.length > 0 && (
                  <div className="mb-6">
                    <h2 className="text-lg font-bold text-blue-800 border-b border-slate-200 pb-2 mb-4">TABLA COMPARATIVA DETALLADA</h2>
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-300 p-2 text-left bg-slate-500 text-white font-semibold w-[22%]">ÍTEM EVALUADO</th>
                          <th className="border border-slate-300 p-2 text-left bg-blue-600 text-white font-semibold w-[26%]">PÓLIZA ACTUAL</th>
                          <th className="border border-slate-300 p-2 text-left bg-green-600 text-white font-semibold w-[26%]">RENOVACIÓN</th>
                          <th className="border border-slate-300 p-2 text-left bg-amber-600 text-white font-semibold w-[26%]">OBSERVACIONES</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.tabla_comparativa.slice(0, 10).map((row, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 p-2 font-semibold text-slate-900 align-top">{row.item}</td>
                            <td className="border border-slate-200 p-2 text-slate-700 align-top bg-blue-50/50">{isMeaningfulValue(row.poliza_a) ? row.poliza_a : "—"}</td>
                            <td className="border border-slate-200 p-2 text-slate-700 align-top bg-green-50/50">{isMeaningfulValue(row.poliza_b) ? row.poliza_b : "—"}</td>
                            <td className="border border-slate-200 p-2 text-amber-800 italic align-top bg-amber-50/50">{row.observacion || "-"}</td>
                          </tr>
                        ))}
                        {/* Coberturas en la vista previa */}
                        {comparison.detalle_coberturas?.length > 0 && (
                          <>
                            <tr>
                              <td colSpan={4} className="bg-[#0C314C] text-white text-center font-bold py-1.5 text-xs border border-slate-300">
                                COBERTURAS COMPARADAS
                              </td>
                            </tr>
                            {comparison.detalle_coberturas.slice(0, 6).map((cob, idx) => {
                              const polAText = [
                                cob.poliza_a_limite && isMeaningfulValue(cob.poliza_a_limite) ? `Lím: ${cob.poliza_a_limite}` : null,
                                cob.poliza_a_deducible && isMeaningfulValue(cob.poliza_a_deducible) ? `Ded: ${cob.poliza_a_deducible}` : null,
                                isMeaningfulValue(cob.poliza_a_prima) ? `Prima: ${cob.poliza_a_prima}` : null,
                              ].filter(Boolean).join(" | ") || "N/A";
                              const polBText = [
                                cob.poliza_b_limite && isMeaningfulValue(cob.poliza_b_limite) ? `Lím: ${cob.poliza_b_limite}` : null,
                                cob.poliza_b_deducible && isMeaningfulValue(cob.poliza_b_deducible) ? `Ded: ${cob.poliza_b_deducible}` : null,
                                isMeaningfulValue(cob.poliza_b_prima) ? `Prima: ${cob.poliza_b_prima}` : null,
                              ].filter(Boolean).join(" | ") || "N/A";
                              return (
                                <tr key={`prev-cob-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                  <td className="border border-slate-200 p-2 font-semibold text-indigo-900 align-top">{cob.cobertura}</td>
                                  <td className="border border-slate-200 p-2 text-slate-700 align-top bg-blue-50/50">{polAText}</td>
                                  <td className="border border-slate-200 p-2 text-slate-700 align-top bg-green-50/50">{polBText}</td>
                                  <td className="border border-slate-200 p-2 text-amber-800 italic align-top bg-amber-50/50">
                                    {cob.poliza_a_limite !== cob.poliza_b_limite || cob.poliza_a_deducible !== cob.poliza_b_deducible ? "Diferencias" : "-"}
                                  </td>
                                </tr>
                              );
                            })}
                            {comparison.detalle_coberturas.length > 6 && (
                              <tr>
                                <td colSpan={4} className="text-center text-xs text-slate-500 italic p-1">
                                  ... y {comparison.detalle_coberturas.length - 6} coberturas más en el PDF completo
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                      </tbody>
                    </table>
                    {comparison.tabla_comparativa.length > 10 && (
                      <p className="text-xs text-slate-500 mt-2 italic">... y {comparison.tabla_comparativa.length - 10} ítems más en el PDF completo</p>
                    )}
                  </div>
                )}

                {/* Dictamen Preview */}
                {comparison?.dictamen_pericial?.recomendacion_final && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h2 className="text-lg font-bold text-green-800 mb-2">RECOMENDACIÓN FINAL</h2>
                    <p className="text-green-700">{comparison.dictamen_pericial.recomendacion_final}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="border-t border-slate-200 pt-4 mt-6 text-center">
                  <p className="text-xs text-slate-400">
                    Generado por QAsesores SRL • {new Date().toLocaleString('es-ES')}
                  </p>
                </div>
              </div>
            </ScrollArea>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Cerrar
              </Button>
              <Button
                onClick={() => {
                  downloadPDF();
                  setShowPreview(false);
                }}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Diálogo: ofrecer crear ticket al guardar informe con diferencias */}
        <Dialog open={ticketPrompt} onOpenChange={(v) => { if (!creandoTicket) setTicketPrompt(v); }}>
          <DialogContent data-testid="comparison-ticket-prompt-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#193966]">
                <MessageCircleQuestion className="w-5 h-5" />
                Se encontraron diferencias en la comparación
              </DialogTitle>
              <DialogDescription>
                Esta comparación tiene diferencias entre la póliza actual y la renovación.
                ¿Desea crear un ticket en Mesa de Ayuda (Solicitud Departamento Riesgos Generales →
                Revisión Comparación) con el resumen de diferencias adjunto en PDF? El ticket quedará
                asignado a usted como sustento de auditoría.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:justify-end">
              <Button
                variant="outline"
                disabled={creandoTicket}
                onClick={() => handleDecisionTicket(false)}
                data-testid="comparison-ticket-decline-btn"
              >
                No, gracias
              </Button>
              <Button
                disabled={creandoTicket}
                onClick={() => handleDecisionTicket(true)}
                data-testid="comparison-ticket-accept-btn"
                className="bg-[#193966] hover:bg-[#142c4f] text-white"
              >
                {creandoTicket ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Ticket className="w-4 h-4 mr-2" />
                )}
                Sí, crear ticket
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}


function InformeEjecutivoUI({ informe }) {
  if (!informe) return null;

  const impactColors = {
    alto: "bg-red-100 text-red-700 border-red-200",
    medio: "bg-amber-100 text-amber-700 border-amber-200",
    bajo: "bg-green-100 text-green-700 border-green-200",
  };
  const changeIcons = {
    aumento: { icon: "ArrowUp", color: "text-red-500", label: "Aumento" },
    disminucion: { icon: "ArrowDown", color: "text-green-500", label: "Disminucion" },
    nuevo: { icon: "Plus", color: "text-blue-500", label: "Nuevo" },
    eliminado: { icon: "X", color: "text-red-600", label: "Eliminado" },
    alerta: { icon: "Alert", color: "text-amber-500", label: "Alerta" },
    sin_cambio: { icon: "Minus", color: "text-slate-400", label: "Sin cambio" },
  };

  return (
    <div className="space-y-6" data-testid="informe-ejecutivo-content">
      {/* Resumen General: eliminado por requerimiento del cliente */}

      {/* Puntuaciones */}
      {(informe.puntuacion_poliza_a || informe.puntuacion_poliza_b) && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-blue-600 text-white rounded-lg p-4 text-center">
            <p className="text-xs font-medium opacity-80 uppercase">Poliza Actual</p>
            <p className="text-3xl font-bold mt-1">{informe.puntuacion_poliza_a || "?"}<span className="text-sm font-normal opacity-60">/10</span></p>
            <p className="text-[10px] opacity-70 mt-1">Calificación (escala 0-10)</p>
          </div>
          <div className="bg-green-600 text-white rounded-lg p-4 text-center">
            <p className="text-xs font-medium opacity-80 uppercase">Renovacion</p>
            <p className="text-3xl font-bold mt-1">{informe.puntuacion_poliza_b || "?"}<span className="text-sm font-normal opacity-60">/10</span></p>
            <p className="text-[10px] opacity-70 mt-1">Calificación (escala 0-10)</p>
          </div>
          <div className="bg-purple-600 text-white rounded-lg p-4 text-center">
            <p className="text-xs font-medium opacity-80 uppercase">Veredicto</p>
            <p className="text-sm font-bold mt-2">{informe.veredicto || "N/A"}</p>
            <p className="text-[10px] opacity-70 mt-1">Recomendación final</p>
          </div>
        </div>
      )}

      {/* Hallazgos Principales */}
      {informe.hallazgos?.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wide flex items-center gap-2">
            <span className="w-1 h-4 bg-blue-600 rounded-full" />
            Hallazgos Principales
          </h3>
          <div className="space-y-2">
            {informe.hallazgos.map((h, i) => {
              const change = changeIcons[h.tipo_cambio] || changeIcons.sin_cambio;
              const impact = impactColors[h.impacto] || impactColors.bajo;
              return (
                <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`hallazgo-${i}`}>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${impact}`}>
                    {h.impacto?.toUpperCase()}
                  </span>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-600 uppercase">
                      <span className={change.color}>{change.label}</span> - {h.categoria}
                    </p>
                    <p className="text-sm text-slate-700 mt-0.5">{h.descripcion}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Diferencias Clave (solo comparación de primas) */}
      {filterDiferenciasPrima(informe.diferencias_clave).length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wide flex items-center gap-2">
            <span className="w-1 h-4 bg-amber-500 rounded-full" />
            Diferencias Clave
          </h3>
          <ol className="space-y-2 list-decimal list-inside">
            {filterDiferenciasPrima(informe.diferencias_clave).map((diff, i) => (
              <li key={i} className="text-sm text-slate-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
                {diff}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Recomendación Ejecutiva */}
      {informe.recomendacion_ejecutiva && (
        <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
          <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-2">Recomendacion Ejecutiva</h3>
          <p className="text-sm text-slate-700 leading-relaxed">{informe.recomendacion_ejecutiva}</p>
        </div>
      )}
    </div>
  );
}
